// src/pages/PaymentFraud.jsx
//
// Payment Fraud Intelligence module — one page, five tabs.
//
// Theme: matches the rest of the dashboard (sky/dark-navy tokens, h-12
// card-header strips with uppercase eyebrows, Recorded Future-style calm
// chart palette). Page bg #000414, surface #0a1224, border sky-900/30,
// accent sky-500.
//
// All data is mocked. Every "// TODO: API" comment marks a real fetch point.
// Full PAN and CVV are never rendered — only bin6 + last_four with dots.
//
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  ChevronDown, ChevronUp, X, Download,
  Plus, Bell, ExternalLink, Filter, Copy, Pencil, Trash2,
  ShieldAlert, ArrowUpRight, ArrowDownRight, Activity,
  CreditCard, Globe, Building2, Eye, EyeOff, Check, Search, Info,
} from "lucide-react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { Tooltip as RTooltip } from "react-tooltip";
import { scaleSqrt } from "d3-scale";
import { geoCentroid } from "d3-geo";
import Sidebar from "../components/Sidebar";
import OrgMemberPicker from "../components/OrgMemberPicker";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { redirectToLogin } from "../utils/auth";
import { useNavigate } from "react-router-dom";
import { usePaymentFraudAccess } from "../utils/brandProtection";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO-2 → numeric (matches topojson `geo.id`). Lifted from InfectionDashboard.
const ISO2_TO_NUMERIC = {
  AD:"020",AE:"784",AF:"004",AG:"028",AI:"660",AL:"008",AM:"051",AO:"024",
  AR:"032",AS:"016",AT:"040",AU:"036",AW:"533",AX:"248",AZ:"031",BA:"070",
  BB:"052",BD:"050",BE:"056",BF:"854",BG:"100",BH:"048",BI:"108",BJ:"204",
  BL:"652",BM:"060",BN:"096",BO:"068",BQ:"535",BR:"076",BS:"044",BT:"064",
  BV:"074",BW:"072",BY:"112",BZ:"084",CA:"124",CC:"166",CD:"180",CF:"140",
  CG:"178",CH:"756",CI:"384",CK:"184",CL:"152",CM:"120",CN:"156",CO:"170",
  CR:"188",CU:"192",CV:"132",CW:"531",CX:"162",CY:"196",CZ:"203",DE:"276",
  DJ:"262",DK:"208",DM:"212",DO:"214",DZ:"012",EC:"218",EE:"233",EG:"818",
  EH:"732",ER:"232",ES:"724",ET:"231",FI:"246",FJ:"242",FK:"238",FM:"583",
  FO:"234",FR:"250",GA:"266",GB:"826",GD:"308",GE:"268",GF:"254",GG:"831",
  GH:"288",GI:"292",GL:"304",GM:"270",GN:"324",GP:"312",GQ:"226",GR:"300",
  GS:"239",GT:"320",GU:"316",GW:"624",GY:"328",HK:"344",HM:"334",HN:"340",
  HR:"191",HT:"332",HU:"348",ID:"360",IE:"372",IL:"376",IM:"833",IN:"356",
  IO:"086",IQ:"368",IR:"364",IS:"352",IT:"380",JE:"832",JM:"388",JO:"400",
  JP:"392",KE:"404",KG:"417",KH:"116",KI:"296",KM:"174",KN:"659",KP:"408",
  KR:"410",KW:"414",KY:"136",KZ:"398",LA:"418",LB:"422",LC:"662",LI:"438",
  LK:"144",LR:"430",LS:"426",LT:"440",LU:"442",LV:"428",LY:"434",MA:"504",
  MC:"492",MD:"498",ME:"499",MF:"663",MG:"450",MH:"584",MK:"807",ML:"466",
  MM:"104",MN:"496",MO:"446",MP:"580",MQ:"474",MR:"478",MS:"500",MT:"470",
  MU:"480",MV:"462",MW:"454",MX:"484",MY:"458",MZ:"508",NA:"516",NC:"540",
  NE:"562",NF:"574",NG:"566",NI:"558",NL:"528",NO:"578",NP:"524",NR:"520",
  NU:"570",NZ:"554",OM:"512",PA:"591",PE:"604",PF:"258",PG:"598",PH:"608",
  PK:"586",PL:"616",PM:"666",PN:"612",PR:"630",PS:"275",PT:"620",PW:"585",
  PY:"600",QA:"634",RE:"638",RO:"642",RS:"688",RU:"643",RW:"646",SA:"682",
  SB:"090",SC:"690",SD:"729",SE:"752",SG:"702",SH:"654",SI:"705",SJ:"744",
  SK:"703",SL:"694",SM:"674",SN:"686",SO:"706",SR:"740",SS:"728",ST:"678",
  SV:"222",SX:"534",SY:"760",SZ:"748",TC:"796",TD:"148",TF:"260",TG:"768",
  TH:"764",TJ:"762",TK:"772",TL:"626",TM:"795",TN:"788",TO:"776",TR:"792",
  TT:"780",TV:"798",TW:"158",TZ:"834",UA:"804",UG:"800",UM:"581",US:"840",
  UY:"858",UZ:"860",VA:"336",VC:"670",VE:"862",VG:"092",VI:"850",VN:"704",
  VU:"548",WF:"876",WS:"882",XK:"412",YE:"887",YT:"175",ZA:"710",ZM:"894",
  ZW:"716",
};

/* Network badges — muted RF-style entity chips. Each network keeps a hint
   of its brand hue (VISA blue, MC warm, etc.) but the saturation is dialled
   down so a row full of badges doesn't compete with severity colors. */
const NETWORK_STYLE = {
  VISA:       { bg: "rgba(28,87,186,0.18)",  text: "#8eb3ff", ring: "rgba(56,114,224,0.45)" },
  MASTERCARD: { bg: "rgba(155,67,40,0.18)",  text: "#f0a48b", ring: "rgba(184,94,68,0.45)" },
  AMEX:       { bg: "rgba(56,114,224,0.12)", text: "#9ec2ec", ring: "rgba(91,134,184,0.40)" },
  DISCOVER:   { bg: "rgba(166,98,32,0.18)",  text: "#f0b878", ring: "rgba(190,124,58,0.45)" },
  JCB:        { bg: "rgba(166,52,82,0.18)",  text: "#f08fa1", ring: "rgba(190,80,108,0.45)" },
  DINERS:     { bg: "rgba(96,104,118,0.18)", text: "#c1c7d2", ring: "rgba(120,128,142,0.45)" },
  UNIONPAY:   { bg: "rgba(40,98,158,0.16)",  text: "#88b6e1", ring: "rgba(70,128,182,0.40)" },
  MAESTRO:    { bg: "rgba(40,98,158,0.16)",  text: "#88b6e1", ring: "rgba(70,128,182,0.40)" },
  RUPAY:      { bg: "rgba(36,138,90,0.18)",  text: "#8edcaf", ring: "rgba(66,166,118,0.45)" },
  MIR:        { bg: "rgba(36,138,128,0.18)", text: "#8eddd1", ring: "rgba(66,166,156,0.45)" },
  UNKNOWN:    { bg: "rgba(64,72,82,0.18)",   text: "#a3a3a3", ring: "rgba(96,104,114,0.40)" },
};

/* Recharts series — sky/cyan family + emerald, like the rest of the app's
   ranked-bar charts. Used by timeseries / single-series charts. */
const SERIES_COLORS = ["#0ea5e9", "#0891b2", "#0e7490", "#2b8fd4", "#7cc4f5", "#10b981", "#a16207", "#6d28d9"];

/* Per-network donut colors — each network gets its brand-aware hue at
   moderate saturation so the slices stay visually distinct without being
   demo-rainbow. Used by the Network distribution donut on Overview. */
const NETWORK_PIE_COLOR = {
  VISA:       "#3b6fc8",  // Visa blue
  MASTERCARD: "#d68450",  // MC warm orange
  AMEX:       "#5fa3d1",  // AMEX teal-blue
  DISCOVER:   "#e89544",  // Discover amber
  JCB:        "#c95a6f",  // JCB pink-red
  DINERS:     "#8d96a3",  // Diners silver-gray
  UNIONPAY:   "#c84047",  // UnionPay red
  MAESTRO:    "#6a7fb5",  // Maestro muted blue
  RUPAY:      "#3fa56a",  // RuPay green
  MIR:        "#4eb09f",  // MIR turquoise
  UNKNOWN:    "#5d6470",  // Neutral fallback
};

/* Tier donut colors — six conceptual categories. Sky for the default
   "standard" tier, gold for premium, purple for business, teal for prepaid,
   neutral for unknown / other so the donut reads at a glance. */
const TIER_PIE_COLOR = {
  standard: "#3b8fd4",  // Sky — the baseline
  premium:  "#b78e3a",  // Muted gold
  business: "#7c5db2",  // Muted purple
  prepaid:  "#4eb09f",  // Teal
  unknown:  "#5d6470",  // Neutral gray
  other:    "#8a7b6c",  // Warm neutral
};

/* Country code → flag emoji */
const flag = (cc) =>
  cc && cc.length === 2
    ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))
    : "🏳";

/* ──────────────────────────────────────────────────────────────────────────
   MOCK DATA GENERATION
   ────────────────────────────────────────────────────────────────────────── */

const ISSUERS = [
  { bank: "Bank of America, N.A.",   country: "US" },
  { bank: "JPMorgan Chase Bank",     country: "US" },
  { bank: "Wells Fargo Bank, N.A.",  country: "US" },
  { bank: "Capital One, N.A.",       country: "US" },
  { bank: "HDFC Bank Ltd.",          country: "IN" },
  { bank: "Yes Bank Ltd.",           country: "IN" },
  { bank: "Westpac Banking Corp.",   country: "AU" },
  { bank: "Commonwealth Bank",       country: "AU" },
  { bank: "Al Rajhi Bank",           country: "SA" },
  { bank: "Banco de Credito del Peru", country: "PE" },
  { bank: "Lloyds Bank PLC",         country: "GB" },
  { bank: "Yorkshire Bank",          country: "GB" },
  { bank: "Banco Santander S.A.",    country: "ES" },
  { bank: "Sberbank of Russia",      country: "RU" },
  { bank: "Deutsche Bank AG",        country: "DE" },
  { bank: "BNP Paribas",             country: "FR" },
];

/* Network distribution + realistic BIN prefix table per spec. */
const NETWORK_DEFS = [
  { net: "VISA",       brand: "visa",       weight: 40, binStarts: ["4"] },
  { net: "MASTERCARD", brand: "mastercard", weight: 28, binStarts: ["51", "52", "53", "54", "55", "2221", "2720"] },
  { net: "AMEX",       brand: "amex",       weight: 8,  binStarts: ["34", "37"] },
  { net: "DISCOVER",   brand: "discover",   weight: 4,  binStarts: ["6011", "65"] },
  { net: "JCB",        brand: "jcb",        weight: 4,  binStarts: ["3528", "3589"] },
  { net: "RUPAY",      brand: "rupay",      weight: 7,  binStarts: ["60", "65", "81"] },
  { net: "MIR",        brand: "mir",        weight: 4,  binStarts: ["2200", "2204"] },
  { net: "UNIONPAY",   brand: "unionpay",   weight: 3,  binStarts: ["62"] },
  { net: "DINERS",     brand: "diners",     weight: 1,  binStarts: ["36"] },
  { net: "MAESTRO",    brand: "maestro",    weight: 1,  binStarts: ["50", "58"] },
];

const CARD_TYPES = [
  { v: "credit",  w: 55 },
  { v: "debit",   w: 35 },
  { v: "prepaid", w: 8 },
  { v: "unknown", w: 2 },
];

// Tiny seeded PRNG so mock data is stable across renders.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const rng = mulberry32(20260530);

const weightedPick = (list, keyW = "w", keyV = "v") => {
  const total = list.reduce((s, x) => s + x[keyW], 0);
  let r = rng() * total;
  for (const x of list) { r -= x[keyW]; if (r <= 0) return x[keyV] ?? x; }
  return list[list.length - 1][keyV] ?? list[list.length - 1];
};

function generateMockCards(n = 240) {
  const out = [];
  const now = Date.now();
  const DAY = 86400000;
  for (let i = 0; i < n; i++) {
    const netDef = weightedPick(NETWORK_DEFS.map((d) => ({ w: d.weight, v: d })));
    const issuer = ISSUERS[Math.floor(rng() * ISSUERS.length)];
    const start = netDef.binStarts[Math.floor(rng() * netDef.binStarts.length)];
    let bin = start;
    while (bin.length < 6) bin += Math.floor(rng() * 10);
    const bin6 = bin.slice(0, 6);
    const bin8 = rng() < 0.7 ? bin6 + String(Math.floor(rng() * 100)).padStart(2, "0") : null;
    const last4 = String(Math.floor(rng() * 10000)).padStart(4, "0");
    const exposedDaysAgo = Math.floor(rng() * 90);
    const exposedAt = now - exposedDaysAgo * DAY - Math.floor(rng() * DAY);
    const importedAt = exposedAt + Math.floor(rng() * DAY * 2);
    // 70% currently-valid
    const valid = rng() < 0.7;
    let expMonth, expYear;
    if (valid) {
      expMonth = 1 + Math.floor(rng() * 12);
      expYear = 2026 + Math.floor(rng() * 5);
    } else {
      expMonth = 1 + Math.floor(rng() * 12);
      expYear = 2020 + Math.floor(rng() * 5); // 2020-2024
    }
    out.push({
      id: `c_${i.toString(36)}`,
      bin6,
      bin8,
      last_four: last4,
      expiry_month: expMonth,
      expiry_year: expYear,
      is_valid_expiry: valid,
      card_network: netDef.net,
      card_brand: netDef.brand,
      card_type: weightedPick(CARD_TYPES),
      issuer_bank: issuer.bank,
      issuer_country: issuer.country,
      imported_at: importedAt,
      exposed_at: exposedAt,
      source_log_id: 100000 + Math.floor(rng() * 9000000),
    });
  }
  // Sort newest first by exposed_at
  out.sort((a, b) => b.exposed_at - a.exposed_at);
  return out;
}

const MOCK_CARDS = generateMockCards(240);

/* ──────────────────────────────────────────────────────────────────────────
   FORMATTERS
   ────────────────────────────────────────────────────────────────────────── */

// Overview endpoint (server pre-aggregates by `range`; backend ignores
// `search` for now so we don't bother sending it).
const OVERVIEW_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/get_overview.php";

// Card Information table endpoint — server-side filter, sort, and paging.
// See backend docs for the exact param contract; we mirror it 1:1 below.
const CARDS_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/get_cards.php";

// Issuers leaderboard endpoint — server-side sort + paging. Returns rows
// with absolute `rank`, `total_exposed`, `new_30d`, `delta_30d`,
// `currently_valid`, and `networks`.
const ISSUERS_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/get_issuers.php";

// BIN lookup endpoint — accepts a single 6-digit BIN in the `bin` query
// param. Response: { success, bins_requested, api_calls_made, quota,
// rate_limit, results: [{ bin, found, source, ...metadata }] }.
const BIN_LOOKUP_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/get_bin_lookup.php";

// Add to card watchlist endpoint. Accepts entry_type = "bin" | "issuer";
// entry_value is a single BIN, a BIN range ("424200-424299"), or an
// issuer name. Optional: secondary_notification_email, label.
const ADD_CARD_WATCHLIST_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/add_card_watchlist.php";

// List card-watchlist entries. Response shape:
//   { success, quota:{used,limit,remaining}, total, entries:[...] }
// Each entry: id, entry_type, entry_value, label, notify_email,
// secondary_notification_email, is_active, created_at, updated_at,
// last_scan_at, total_alerts, total_cards_matched, last_alert_at.
const LIST_CARD_WATCHLIST_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/list_card_watchlist.php";

// Mutate (delete/pause/resume) a card-watchlist entry. POST body:
//   { id: <number>, action?: "pause" | "resume" }   — action omitted = delete
// Error shape (same for not-found vs cross-org, anti-enumeration):
//   { success: false, message: "Watchlist entry not found" }
const DELETE_CARD_WATCHLIST_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/delete_card_watchlist.php";

// Edit an existing watchlist entry. Partial update — POST body carries
// only the fields the caller wants to change. Mutable fields:
//   label, secondary_notification_email, is_active
// entry_type / entry_value / notify_email are immutable from the UI.
// Response: { success: true, entry: <full updated entry> }
const EDIT_CARD_WATCHLIST_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/edit_card_watchlist.php";

// List triggered card alerts. Optional filters in POST body:
//   { watchlist_id?, since?, until?, delivered_only?, page?, page_size? }
// Response: { success, stats:{total, delivered_count, failed_count,
// total_cards_matched}, page, page_size, alerts:[...] }.
// Each alert: id, cards_matched, matched_log_ids_count, sample_bin6s[],
// notify_email, secondary_notification_email, delivered, error_message,
// sendgrid_message_id, sent_at (unix sec), watchlist:{id, entry_type,
// entry_value, label}.
const LIST_CARD_ALERTS_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/list_card_alerts.php";

// Single-alert detail fetch. POST body: { id, cards_page?, cards_page_size? }.
// Returns the full alert object + paginated `cards.results` with per-card
// data (bin6/8, last_four, issuer_bank, issuer_country, card_network,
// card_type, expiry_month, expiry_year, is_valid_expiry_now, exposed_at,
// source_log_id, card_index) plus the watchlist context (is_active,
// created_at, last_scan_at, added_by).
const GET_CARD_ALERT_URL =
  "https://whiteintel.io/api/regular/app/v2/paymentfraud/get_card_alert.php";


function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

// Format a JSON error body into a single human-readable string. Covers
// payloads like { message, errors:[...] } (e.g. validation failures from
// the card-watchlist endpoints) as well as the simpler { error } / plain
// message variants. Detail lines get bulleted on their own rows so the
// renderer can use `whitespace-pre-line` to lay them out.
function formatApiError(payload, fallback) {
  const base = payload?.message || payload?.error || fallback || "Request failed.";
  const details = Array.isArray(payload?.errors) ? payload.errors.filter(Boolean) : [];
  if (!details.length) return base;
  return `${base}\n• ${details.join("\n• ")}`;
}

const fmtRelative = (ts) => {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(ts).toLocaleDateString();
};
const fmtAbsolute = (ts) =>
  new Date(ts).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
const fmtNum = (n) => (n ?? 0).toLocaleString();
const maskedPan = (c) => `${c.bin6}••••${c.last_four}`;
const fmtExpiry = (m, y) => {
  // Backend can leave one or both fields null when the original log didn't
  // include an expiry. Render a dash instead of "null/ll".
  if (m == null || y == null) return "—";
  return `${String(m).padStart(2, "0")}/${String(y).slice(-2)}`;
};

/* ──────────────────────────────────────────────────────────────────────────
   ATOMS
   ────────────────────────────────────────────────────────────────────────── */

// Three-state dot: emerald = valid, amber = expired, neutral = unknown
// expiry (backend didn't have month/year). Without the third state the UI
// said "Expired" for any card whose log didn't include an expiry, which
// isn't true — we just don't know.
function ValidityDot({ valid, unknown = false, className = "" }) {
  const state = unknown ? "Expiry unknown" : valid ? "Currently valid" : "Expired";
  const color = unknown
    ? "bg-neutral-500"
    : valid
    ? "bg-emerald-500"
    : "bg-amber-500";
  return (
    <span
      role="img"
      aria-label={state}
      title={state}
      className={`inline-block h-2 w-2 rounded-full shrink-0 ${color} ${className}`}
    />
  );
}

function NetworkBadge({ net, size = "md" }) {
  const s = NETWORK_STYLE[net] || NETWORK_STYLE.UNKNOWN;
  const sizeCls = size === "sm"
    ? "text-[9.5px] px-1.5 py-0.5"
    : "text-[10.5px] px-2 py-0.5";
  return (
    <span
      className={`inline-flex items-center rounded font-semibold uppercase tracking-wider ring-1 ring-inset ${sizeCls}`}
      style={{ background: s.bg, color: s.text, "--tw-ring-color": s.ring + "55" }}
    >
      {net}
    </span>
  );
}

function MaskedCardText({ card, className = "", fullyMasked = false }) {
  // When `fullyMasked` is true we render only dots, no BIN6 or last-four.
  // Used by the Card Intelligence "mask all" toggle for screenshot-safe views.
  if (fullyMasked) {
    return (
      <span className={`font-mono tabular-nums text-neutral-500 ${className}`}>
        ••••&nbsp;••••&nbsp;••••
      </span>
    );
  }
  return (
    <span className={`font-mono tabular-nums ${className}`}>
      {card.bin8}
      <span className="text-neutral-600 mx-0.5">••••</span>
      {card.last_four}
    </span>
  );
}

function Country({ cc, showCode = true, className = "" }) {
  // Null / empty country → simple dash rather than the white-flag fallback
  // emoji + a literal "null" code.
  if (!cc) return <span className={`text-neutral-500 ${className}`}>—</span>;
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`}>
      <span className="text-[14px] leading-none">{flag(cc)}</span>
      {showCode && <span className="text-[12px] tabular-nums">{cc}</span>}
    </span>
  );
}

function SurfaceCard({ children, className = "", noPad = false }) {
  // Matches the rest of the dashboard: rounded-md, sky-tinted border,
  // translucent dark-navy fill. Body padding can be disabled when the card
  // contains its own header strip + sectioned body.
  return (
    <div
      className={`rounded-md border border-sky-900/30 bg-[#0a1224]/70 overflow-hidden ${noPad ? "" : "p-5"} ${className}`}
    >
      {children}
    </div>
  );
}

function CardHeader({ icon: Icon, title, right }) {
  // Fixed h-12 strip with an icon + uppercase eyebrow + optional right slot.
  // Matches the InfectionDashboard / Dashboard card-header pattern.
  return (
    <div className="px-4 h-12 shrink-0 border-b border-sky-900/30 bg-[#0b1426]/60 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-sky-300/80" />}
        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-neutral-300 truncate">
          {title}
        </h3>
      </div>
      <div className="shrink-0 whitespace-nowrap">{right}</div>
    </div>
  );
}

function SectionHeader({ title, right, hint }) {
  // Used in-body (above tables, inside padded surface cards). Eyebrow style
  // matches the dashboard's standard "muted neutral-400 uppercase" pattern.
  return (
    <div className="flex items-end justify-between gap-3 mb-3">
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
          {title}
        </div>
        {hint && (
          <div className="mt-0.5 text-[11px] text-neutral-500">
            {hint}
          </div>
        )}
      </div>
      {right}
    </div>
  );
}

function KpiCard({ label, value, delta, deltaDir, sub }) {
  const isUp = deltaDir === "up";
  const isDown = deltaDir === "down";
  const deltaCls = isUp
    ? "text-emerald-400"
    : isDown
    ? "text-red-400"
    : "text-neutral-500";
  return (
    <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 p-4">
      <div className="text-[10.5px] font-medium uppercase tracking-wider text-neutral-400">
        {label}
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-[26px] font-semibold tabular-nums text-neutral-100">
          {value}
        </div>
        {delta != null && (
          <div className={`inline-flex items-center gap-0.5 text-[12px] font-medium tabular-nums ${deltaCls}`}>
            {isUp ? <ArrowUpRight className="h-3.5 w-3.5" /> : isDown ? <ArrowDownRight className="h-3.5 w-3.5" /> : null}
            {delta}
          </div>
        )}
      </div>
      {sub && <div className="mt-1 text-[11px] text-neutral-500">{sub}</div>}
    </div>
  );
}

function SeverityChip({ level }) {
  // Aligned to the dashboard's severity vocabulary (sky / amber / red, ring-1
  // inset). Matches WatchlistEvents and SavedIncidents.
  const map = {
    info:     { text: "Info",     cls: "bg-sky-500/10 text-sky-300 ring-sky-500/30" },
    warning:  { text: "Warning",  cls: "bg-amber-500/10 text-amber-300 ring-amber-500/30" },
    critical: { text: "Critical", cls: "bg-red-500/10 text-red-300 ring-red-500/30" },
  };
  const s = map[level] || map.info;
  return (
    <span
      className={`inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset ${s.cls}`}
    >
      {s.text}
    </span>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <span
        onClick={() => onChange(!checked)}
        className={`relative inline-block h-4 w-7 rounded-full transition-colors ${
          checked ? "bg-sky-500" : "bg-sky-900/50"
        }`}
      >
        <span
          className="absolute top-0.5 h-3 w-3 rounded-full bg-white transition-transform"
          style={{ left: checked ? "14px" : "2px" }}
        />
      </span>
      {label && <span className="text-[12px] text-neutral-400">{label}</span>}
    </label>
  );
}

function Chip({ children, active, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-[11.5px] px-2 py-1 rounded border transition-colors ${
        active
          ? "bg-sky-500/15 border-sky-500/40 text-sky-100"
          : "bg-transparent border-sky-900/40 text-neutral-400 hover:border-sky-700/50 hover:text-neutral-200"
      } ${className}`}
    >
      {children}
    </button>
  );
}

function TextInput({ value, onChange, placeholder, icon: Icon, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      {Icon && (
        <Icon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
      )}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded text-[12px] py-1.5 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 placeholder:text-neutral-500 focus:border-sky-500/50 ${
          Icon ? "pl-8 pr-2" : "px-2"
        }`}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   OVERVIEW TAB
   ────────────────────────────────────────────────────────────────────────── */

function OverviewTab({ range, rangeDays }) {
  // Live data — server-aggregated by `range`. We mirror the spec field names
  // 1:1 so the JSX below didn't need to change; the only tiny adapter is
  // mapping each issuer's `country` → `cc` (for the flag helper) and
  // converting `exposed_at` from epoch seconds → ms (what the fmt helpers
  // expect).
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUpgradeRequired(false);

    const url = `${OVERVIEW_URL}?range=${encodeURIComponent(range)}`;
    fetch(url, { headers: { Authorization: `Bearer ${getJwtToken()}` } })
      .then(async (r) => {
        // 401 / 419 / 440 → session is gone. Drop the user on /login with
        // ?next=<current path> so they return here after signing in.
        if ([401, 419, 440].includes(r.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${r.status}`);
        }
        const json = await r.json().catch(() => ({}));
        // 402 + upgrade_required, or any 403, → tier upsell. 403 is what
        // the backend returns when the org doesn't have the add-on entitled.
        if ((r.status === 402 && json?.upgrade_required) || r.status === 403)
          throw { _upgrade: true };
        if (!r.ok) throw new Error(json?.message || `HTTP ${r.status}`);
        return json;
      })
      .then((json) => {
        if (cancelled) return;
        if (json?.success) setData(json);
        else setError(json?.message || "Failed to load overview.");
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?._upgrade) setUpgradeRequired(true);
        else {
          console.error("Overview fetch error:", e);
          setError(e?.message || "Network error");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [range]);

  const kpis = data?.kpis || {};
  // Two distinct "total" reads:
  //   - lifetimeTotal: total cards ever observed (range-independent). Shown
  //     as the prominent header stat above the KPI strip — conveys the
  //     overall corpus scale at a glance.
  //   - total: cards exposed inside the selected range. Drives KPI #1.
  const lifetimeTotal       = data?.total_card_exposure   ?? 0;
  // currently_valid_total is range-independent — it's the count of cards
  // whose expiry is still current today, across the entire dataset.
  const currentlyValidTotal = data?.currently_valid_total ?? 0;
  const total               = kpis.total_exposed    ?? 0;
  const issuersAffected     = kpis.distinct_issuers ?? 0;

  const seriesData      = data?.exposures_over_time  || [];
  const networkDist     = data?.network_distribution || [];
  const tierDist        = data?.tier_distribution    || [];
  const issuerCountries = data?.issuer_countries     || [];

  // Adapt issuer `country` → `cc` for parity with the existing flag helper.
  const topIssuers = useMemo(
    () => (data?.top_issuers || []).map((d) => ({ ...d, cc: d.country })),
    [data],
  );

  // Tier upsell takes over the tab — same gate the other tabs use.
  if (upgradeRequired) {
    return <UpgradeRequiredCta />;
  }

  return (
    <div className="space-y-4">
      {/* Error banner — calm red ring, dismissible. Shown only when the
          fetch fails; the rest of the tab still renders with empty data
          so the layout doesn't jump. */}
      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-200 flex items-start justify-between gap-3">
          <span className="min-w-0">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-[11px] text-neutral-400 hover:text-white shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Skeletons on every fetch — first load and any range change. Gives
          the user clear "new data incoming" feedback when they swap the
          chip. */}
      {loading ? (
        <OverviewSkeleton />
      ) : (
      <>
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total card exposure" value={fmtNum(lifetimeTotal)} sub="All cards observed to date" />
        <KpiCard label="Currently valid" value={fmtNum(currentlyValidTotal)} sub="Expiry still current today" />
        <KpiCard label={`Cards exposed · ${rangeDays}d`} value={fmtNum(total)} />
        <KpiCard label={`Distinct issuers · ${rangeDays}d`} value={fmtNum(issuersAffected)} />
      </div>

      {/* Timeseries + recent feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <SurfaceCard noPad className="lg:col-span-2">
          <CardHeader icon={Activity} title="Exposures over time" right={<span className="text-[11px] text-neutral-500">Daily total</span>} />
          <div className="px-3 pt-3 pb-2 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={seriesData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <defs>
                  <linearGradient id="pf-exposure-gradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#0ea5e9" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#0ea5e9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(12,74,110,0.25)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#737373", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "rgba(12,74,110,0.30)" }} minTickGap={20} />
                <YAxis tick={{ fill: "#737373", fontSize: 10 }} tickLine={false} axisLine={false} width={36} />
                <Tooltip
                  contentStyle={{
                    background: "#0a1224",
                    border: "1px solid rgba(12,74,110,0.55)",
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#f5f5f5" }}
                  itemStyle={{ color: "#a3a3a3" }}
                  formatter={(v) => [fmtNum(v), "Cards exposed"]}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#0ea5e9"
                  strokeWidth={1.75}
                  fill="url(#pf-exposure-gradient)"
                  activeDot={{ r: 3, stroke: "#0ea5e9", strokeWidth: 1, fill: "#0a1224" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SurfaceCard>

        {/* Network distribution donut — moved here next to the timeseries
            since Recent exposure events is no longer returned by the API. */}
        <SurfaceCard noPad>
          <CardHeader
            icon={CreditCard}
            title="Network distribution"
            right={<span className="text-[11px] text-neutral-500">Last {rangeDays}d</span>}
          />
          <div className="p-4 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={networkDist}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={88}
                  paddingAngle={2}
                  stroke="#0a1224"
                  strokeWidth={2}
                >
                  {networkDist.map((d, i) => (
                    <Cell
                      key={d.name}
                      fill={NETWORK_PIE_COLOR[d.name] || SERIES_COLORS[i % SERIES_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#0a1224", border: "1px solid rgba(12,74,110,0.55)", borderRadius: 4, fontSize: 12 }}
                  labelStyle={{ color: "#f5f5f5" }}
                  itemStyle={{ color: "#a3a3a3" }}
                />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  wrapperStyle={{ fontSize: 11, color: "#a3a3a3", paddingTop: 8 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SurfaceCard>
      </div>

      {/* Row: Top issuers (ranked list) + Tier distribution (donut). Two
          different visualisation types so they read as distinct angles. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <SurfaceCard noPad className="lg:col-span-2">
          <CardHeader
            icon={Building2}
            title="Top issuers"
            right={<span className="text-[11px] text-neutral-500">By cards exposed · {rangeDays}d</span>}
          />
          <div className="px-4 py-3">
            {(() => {
              const maxIssuer = Math.max(1, ...topIssuers.map((d) => d.value));
              const total = topIssuers.reduce((s, d) => s + d.value, 0) || 1;
              return (
                <ul className="divide-y divide-sky-900/20">
                  {topIssuers.map((d, i) => {
                    const w = Math.max(2, (d.value / maxIssuer) * 100);
                    const pct = (d.value / total) * 100;
                    const up = d.delta > 0;
                    const dn = d.delta < 0;
                    return (
                      <li
                        key={d.name}
                        className="grid grid-cols-[20px_minmax(0,1fr)_120px_64px_60px] items-center gap-3 py-2 text-[12px]"
                      >
                        <span className="text-[10.5px] tabular-nums font-mono text-neutral-600">
                          {(i + 1).toString().padStart(2, "0")}
                        </span>
                        <span className="min-w-0 inline-flex items-center gap-2">
                          <span className="text-[13px] leading-none">{flag(d.cc)}</span>
                          <span className="truncate text-neutral-100">{d.name}</span>
                        </span>
                        <div className="h-1 rounded-full bg-sky-900/20 overflow-hidden">
                          <div className="h-full bg-sky-500/80" style={{ width: `${w}%` }} />
                        </div>
                        <span className="text-right tabular-nums text-neutral-100 font-medium">
                          {fmtNum(d.value)}
                          <span className="ml-1 text-neutral-500 font-normal">· {pct.toFixed(1)}%</span>
                        </span>
                        <span
                          className={`inline-flex items-center justify-end gap-0.5 tabular-nums text-[11px] ${
                            up ? "text-red-400" : dn ? "text-emerald-400" : "text-neutral-600"
                          }`}
                          title="Recent half vs prior half of range"
                        >
                          {up ? <ArrowUpRight className="h-3 w-3" /> : dn ? <ArrowDownRight className="h-3 w-3" /> : null}
                          {up ? "+" : ""}{d.delta}%
                        </span>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </div>
        </SurfaceCard>

        {/* Tier distribution donut — replaces Network distribution in this
            row (Network moved up next to the timeseries). Categories are
            backend-defined: standard / premium / business / prepaid /
            unknown / other. */}
        <SurfaceCard noPad>
          <CardHeader
            icon={CreditCard}
            title="Tier distribution"
            right={<span className="text-[11px] text-neutral-500">Last {rangeDays}d</span>}
          />
          <div className="p-4 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={tierDist}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={88}
                  paddingAngle={2}
                  stroke="#0a1224"
                  strokeWidth={2}
                >
                  {tierDist.map((d, i) => (
                    <Cell
                      key={d.name}
                      fill={TIER_PIE_COLOR[d.name] || SERIES_COLORS[i % SERIES_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: "#0a1224", border: "1px solid rgba(12,74,110,0.55)", borderRadius: 4, fontSize: 12 }}
                  labelStyle={{ color: "#f5f5f5" }}
                  itemStyle={{ color: "#a3a3a3", textTransform: "capitalize" }}
                  formatter={(value, name) => [fmtNum(value), name]}
                />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  wrapperStyle={{ fontSize: 11, color: "#a3a3a3", paddingTop: 8, textTransform: "capitalize" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SurfaceCard>
      </div>

      {/* Stolen card map — bubble overlay (RF/Mandiant style). A dim world
          base + sized circles at each active country's centroid; the bubble
          radius is sqrt-scaled so the visual area is proportional to value. */}
      <SurfaceCard noPad>
        <CardHeader
          icon={Globe}
          title="Stolen card map"
          right={<span className="text-[11px] text-neutral-500">Last {rangeDays}d</span>}
        />
        {(() => {
          // Map data comes pre-aggregated as [{ cc, value }]. We just need
          // to index by topojson numeric ID for the choropleth lookup and
          // pick the top 5 for the hotspot strip.
          const valueByNumeric = {};
          let maxValue = 0;
          issuerCountries.forEach(({ cc, value }) => {
            const num = ISO2_TO_NUMERIC[cc];
            if (!num) return;
            valueByNumeric[num] = { iso2: cc, value };
            if (value > maxValue) maxValue = value;
          });
          // Fixed absolute scale so a bubble's size carries consistent
          // meaning across renders ("500 cards always looks the same"), and
          // the upper end is hard-capped at 14px so very hot countries don't
          // swallow neighbours. Sqrt curve → bubble AREA tracks value.
          const BUBBLE_CAP = 2000;
          const bubbleRadius = scaleSqrt()
            .domain([0, BUBBLE_CAP])
            .range([2.5, 14])
            .clamp(true);
          // Backend already sorts issuer_countries by value DESC, so slicing
          // is enough — no need to re-sort here.
          const top5 = issuerCountries.slice(0, 5);

          return (
            <>
              <div className="relative bg-[#020617]">
                <ComposableMap
                  projection="geoEqualEarth"
                  projectionConfig={{ scale: 155 }}
                  width={800}
                  height={420}
                  style={{ width: "100%", height: "auto", background: "#020617" }}
                >
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) => (
                      <>
                        {/* Base layer: all countries in a uniform dim fill so
                            the map reads as a neutral canvas, letting the
                            bubbles carry the signal. */}
                        {geographies.map((geo) => {
                          const numericId = String(geo.id).padStart(3, "0");
                          const entry = valueByNumeric[numericId];
                          const name = geo.properties?.name || entry?.iso2 || "—";
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              data-tooltip-id="pf-geo-tip"
                              data-tooltip-content={
                                entry
                                  ? `${name} · ${fmtNum(entry.value)} cards`
                                  : `${name} · no data`
                              }
                              style={{
                                default: {
                                  fill: "#0e1a2e",
                                  stroke: "#1c2b46",
                                  strokeWidth: 0.4,
                                  outline: "none",
                                },
                                hover: {
                                  fill: "#172742",
                                  stroke: "#2b3f63",
                                  strokeWidth: 0.6,
                                  outline: "none",
                                  cursor: "pointer",
                                },
                                pressed: { fill: "#172742", outline: "none" },
                              }}
                            />
                          );
                        })}

                        {/* Bubble overlay: one circle per active country at
                            its geographic centroid. Sqrt scale → bubble AREA
                            tracks value (perceptually correct). */}
                        {geographies.map((geo) => {
                          const numericId = String(geo.id).padStart(3, "0");
                          const entry = valueByNumeric[numericId];
                          if (!entry) return null;
                          const coords = geoCentroid(geo);
                          if (!coords || !Number.isFinite(coords[0]) || !Number.isFinite(coords[1])) return null;
                          const r = bubbleRadius(entry.value);
                          const name = geo.properties?.name || entry.iso2;
                          return (
                            <Marker
                              key={`m-${geo.rsmKey}`}
                              coordinates={coords}
                              data-tooltip-id="pf-geo-tip"
                              data-tooltip-content={`${name} · ${fmtNum(entry.value)} cards`}
                            >
                              {/* Soft halo for the "intel signal" feel */}
                              <circle
                                r={r * 1.7}
                                fill="#38bdf8"
                                fillOpacity={0.08}
                                stroke="none"
                                pointerEvents="none"
                              />
                              <circle
                                r={r}
                                fill="#38bdf8"
                                fillOpacity={0.55}
                                stroke="#7dd3fc"
                                strokeWidth={0.8}
                                strokeOpacity={0.9}
                              />
                            </Marker>
                          );
                        })}
                      </>
                    )}
                  </Geographies>
                </ComposableMap>
                <RTooltip
                  id="pf-geo-tip"
                  className="!bg-[#0a1224] !text-neutral-100 !text-[11px] !font-mono !rounded-sm !border !border-sky-500/30 !px-2 !py-1"
                />
              </div>

              {/* Bubble-size legend — fixed reference values so the legend
                  carries the same meaning across renders. "≥2k" marks the cap
                  so users know very hot countries don't grow further. */}
              <div className="border-t border-sky-900/30 px-4 py-2.5 flex items-center gap-4 text-[10px] font-mono text-neutral-500 uppercase tracking-wider">
                <span className="shrink-0">Bubble size · cards</span>
                {[
                  { v: 50,   label: "50" },
                  { v: 500,  label: "500" },
                  { v: 2000, label: "≥2k" },
                ].map(({ v, label }) => (
                  <span key={label} className="inline-flex items-center gap-1.5 shrink-0">
                    <svg width={Math.max(8, bubbleRadius(v) * 2 + 2)} height={Math.max(8, bubbleRadius(v) * 2 + 2)}>
                      <circle
                        cx="50%"
                        cy="50%"
                        r={bubbleRadius(v)}
                        fill="#38bdf8"
                        fillOpacity={0.55}
                        stroke="#7dd3fc"
                        strokeWidth={0.8}
                        strokeOpacity={0.9}
                      />
                    </svg>
                    <span className="text-neutral-400 tabular-nums normal-case">{label}</span>
                  </span>
                ))}
              </div>

              {/* Inline hotspot strip */}
              <div className="border-t border-sky-900/30 px-4 py-2.5 flex items-center gap-3 overflow-x-auto">
                <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider shrink-0">
                  Hotspots ·
                </span>
                {top5.map((c) => (
                  <span key={c.cc} className="inline-flex items-center gap-1.5 text-[11px] text-neutral-300 shrink-0">
                    <span className="text-[13px] leading-none">{flag(c.cc)}</span>
                    <span className="tabular-nums text-neutral-400">{c.cc}</span>
                    <span className="font-mono tabular-nums text-neutral-100">{fmtNum(c.value)}</span>
                  </span>
                ))}
              </div>
            </>
          );
        })()}
      </SurfaceCard>
      </>
      )}
    </div>
  );
}

/* Layout-shaped skeleton for the Overview tab — mirrors the real grid so
   the page doesn't jump when data arrives. animate-pulse on sky-tinted
   blocks gives a calm RF-style shimmer (no big spinners). */
function OverviewSkeleton() {
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonKpi key={i} />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
        <SkeletonCard className="lg:col-span-2" height={348} />
        <SkeletonCard height={348} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-4">
        <SkeletonCard className="lg:col-span-2" height={400} />
        <SkeletonCard height={400} />
      </div>
      <div className="mt-4">
        <SkeletonCard height={500} />
      </div>
    </>
  );
}

function SkeletonBar({ className = "" }) {
  return <div className={`bg-sky-900/30 rounded animate-pulse ${className}`} />;
}

function SkeletonKpi() {
  return (
    <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 p-4 space-y-2.5">
      <SkeletonBar className="h-2.5 w-32" />
      <SkeletonBar className="h-8 w-24" />
      <SkeletonBar className="h-2 w-20" />
    </div>
  );
}

function SkeletonCard({ className = "", height = 300 }) {
  return (
    <div
      className={`rounded-md border border-sky-900/30 bg-[#0a1224]/70 flex flex-col overflow-hidden ${className}`}
      style={{ height }}
    >
      <div className="px-4 h-12 border-b border-sky-900/30 bg-[#0b1426]/60 flex items-center shrink-0">
        <SkeletonBar className="h-3 w-40" />
      </div>
      <div className="flex-1 p-4 space-y-3">
        <SkeletonBar className="h-2.5 w-3/4" />
        <SkeletonBar className="h-2.5 w-2/3" />
        <SkeletonBar className="h-2.5 w-5/6" />
        <SkeletonBar className="h-2.5 w-1/2" />
        <SkeletonBar className="h-2.5 w-4/5" />
        <SkeletonBar className="h-2.5 w-3/5" />
      </div>
    </div>
  );
}

/* Issuer single-select for the issuer-bank filter — searchable combobox
   backed by GET /paymentfraud/get_issuers.php?q=<query>. We debounce the
   search so the user can type freely without burning quota, and we
   infinite-scroll the dropdown: when the user reaches the bottom of the
   list we fetch the next page and append. The trigger stores the raw bank
   name (string), so existing URL deep links (`?issuer=<name>`) keep
   working with no change to the filter contract. */
function IssuerPicker({ value, onChange }) {
  const PAGE_SIZE = 50;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);          // initial / replace fetch
  const [loadingMore, setLoadingMore] = useState(false);  // appending next page
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  // Synchronous mirrors of page / loading / hasMore so the scroll handler
  // can decide whether to load another page without racing React's batched
  // setState updates.
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  // Monotonic request id — used to drop stale fetch results when the user
  // types faster than the network responds.
  const reqIdRef = useRef(0);

  const doFetch = async (p, q, append) => {
    const reqId = ++reqIdRef.current;
    loadingRef.current = true;
    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const params = new URLSearchParams();
      params.set("page", String(p));
      params.set("page_size", String(PAGE_SIZE));
      params.set("sort", "total_exposed");
      params.set("sort_dir", "desc");
      if (q.trim()) params.set("q", q.trim());

      const r = await fetch(`${ISSUERS_URL}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getJwtToken()}` },
      });
      if ([401, 419, 440].includes(r.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${r.status}`);
      }
      const json = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(json?.message || `HTTP ${r.status}`);

      if (reqId !== reqIdRef.current) return; // stale — newer fetch superseded us

      const newRows = json?.rows || [];
      const more = newRows.length >= PAGE_SIZE;
      hasMoreRef.current = more;
      setHasMore(more);
      if (append) setResults((prev) => [...prev, ...newRows]);
      else setResults(newRows);
    } catch (e) {
      if (reqId !== reqIdRef.current) return;
      console.error("Issuer search error:", e);
      if (!append) setResults([]);
    } finally {
      if (reqId === reqIdRef.current) {
        loadingRef.current = false;
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    }
  };

  // Initial / query-change fetch — debounced. Resets paging.
  //
  // Search rules (enterprise-style — keeps incidental keystrokes off the
  // wire while still feeling responsive for browse mode):
  //   - Empty query             → 350ms, fetch top 50 by total_exposed
  //   - 1 or 2 characters       → no fetch, show "type at least 3" hint
  //   - 3+ characters           → 2 second debounce, then fetch with q=
  const MIN_QUERY_LEN = 3;
  const SEARCH_DEBOUNCE_MS = 2000;
  const BROWSE_DEBOUNCE_MS = 350;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    pageRef.current = 1;
    hasMoreRef.current = true;
    setHasMore(true);
    setResults([]);

    const trimmedQ = query.trim();

    // Below the minimum length → don't fetch; the render shows a hint.
    if (trimmedQ.length > 0 && trimmedQ.length < MIN_QUERY_LEN) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const delay = trimmedQ.length === 0 ? BROWSE_DEBOUNCE_MS : SEARCH_DEBOUNCE_MS;

    const t = setTimeout(() => {
      if (cancelled) return;
      doFetch(1, query, false);
    }, delay);

    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query]);

  // Scroll-driven pagination. We fire as soon as the user is within 80px
  // of the bottom; loadingRef + hasMoreRef guard against double-firing.
  const handleScroll = (e) => {
    if (loadingRef.current || !hasMoreRef.current) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      const nextPage = pageRef.current + 1;
      pageRef.current = nextPage;
      doFetch(nextPage, query, true);
    }
  };

  // Position the portal'd dropdown under the trigger, follow scroll/resize.
  useEffect(() => {
    if (!open) return;
    const updatePos = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); setQuery(""); }
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (name) => {
    onChange(name);
    setQuery("");
    setOpen(false);
  };

  const inputValue = open ? query : (value || "");

  return (
    <div ref={triggerRef} className="relative">
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search issuers…"
          className="w-full rounded-md text-[12px] py-1.5 pl-3 pr-14 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 placeholder:text-neutral-500 focus:border-sky-500/50"
        />
        <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {value && (
            <button
              type="button"
              onClick={() => { onChange(""); setQuery(""); }}
              title="Clear"
              className="p-1 rounded text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            tabIndex={-1}
            className="p-1 rounded text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10"
          >
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          onScroll={handleScroll}
          className="fixed z-[1000] max-h-[320px] overflow-y-auto rounded-md border border-sky-900/40 bg-[#0a1224] shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
        >
          {/* Short query → hint, no network call (saves a request while the
              user is still typing the search term). */}
          {query.trim().length > 0 && query.trim().length < MIN_QUERY_LEN ? (
            <div className="px-3 py-2.5 text-[12px] text-neutral-500">
              Type at least {MIN_QUERY_LEN} characters to search.
            </div>
          ) : loading ? (
            <div className="px-3 py-2.5 text-[12px] text-neutral-500">Searching…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2.5 text-[12px] text-neutral-500">
              {query.trim() ? "No matches." : "No issuers."}
            </div>
          ) : (
            <>
              <ul>
                {results.map((r, i) => {
                  const isActive = value && value.toLowerCase() === (r.name || "").toLowerCase();
                  return (
                    <li key={`${r.name}-${r.country}-${i}`}>
                      <button
                        type="button"
                        onClick={() => select(r.name)}
                        className={`w-full text-left px-3 py-1.5 text-[12.5px] flex items-center gap-2 transition-colors ${
                          isActive
                            ? "bg-sky-500/15 text-sky-100"
                            : "text-neutral-200 hover:bg-sky-500/10"
                        }`}
                      >
                        <span className="text-[13px] leading-none shrink-0">{flag(r.country)}</span>
                        <span className="flex-1 min-w-0 truncate">{r.name}</span>
                        <span className="tabular-nums text-[10.5px] text-neutral-500 font-mono shrink-0">
                          {fmtNum(r.total_exposed)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {/* Append-mode loading indicator + end marker */}
              {loadingMore && (
                <div className="px-3 py-2 text-[11.5px] text-sky-300 text-center inline-flex items-center justify-center gap-1.5 w-full">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse" />
                  Loading more…
                </div>
              )}
              {!loadingMore && !hasMore && results.length >= PAGE_SIZE && (
                <div className="px-3 py-2 text-[10.5px] text-neutral-600 text-center uppercase tracking-wider">
                  End of list
                </div>
              )}
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* Country multi-select for the issuer-country filter — searchable combobox
   over all 200+ ISO-3166-1 alpha-2 codes the backend understands. Selected
   countries display inline as removable chips; the popover lets users
   filter by name or code and toggle multiple in one open. */
function CountryMultiSelect({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // Position is computed on open and re-computed on scroll/resize so the
  // portal'd dropdown follows the trigger without being clipped by any
  // ancestor's overflow:hidden (the filter SurfaceCard has it).
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Full list, built once. Names come from Intl.DisplayNames (no extra
  // dataset shipped); codes that the Intl API doesn't know fall back to the
  // raw ISO code so the row is still pickable.
  const allCountries = useMemo(() => {
    const dn =
      typeof Intl !== "undefined" && Intl.DisplayNames
        ? new Intl.DisplayNames(["en"], { type: "region" })
        : null;
    return Object.keys(ISO2_TO_NUMERIC)
      .map((cc) => ({
        cc,
        name: (dn && dn.of(cc)) || cc,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allCountries;
    return allCountries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.cc.toLowerCase().includes(q),
    );
  }, [allCountries, query]);

  // Track the trigger's screen position so the portal dropdown stays
  // anchored under it across scrolling / window resize.
  useEffect(() => {
    if (!open) return;
    const updatePos = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    updatePos();
    window.addEventListener("scroll", updatePos, true);
    window.addEventListener("resize", updatePos);
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      // Outside-click handler must account for the portal'd dropdown — a
      // click inside it shouldn't close it.
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); setQuery(""); }
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (cc) =>
    onChange(selected.includes(cc) ? selected.filter((x) => x !== cc) : [...selected, cc]);

  return (
    <div ref={triggerRef} className="relative">
      {/* Trigger: chip strip + inline search. Click anywhere to focus the
          search input and open the dropdown. */}
      <div
        onClick={() => setOpen(true)}
        className="min-h-[34px] w-full rounded-md border border-sky-900/40 bg-[#000414] text-[12px] py-1 px-1.5 flex items-center gap-1 flex-wrap cursor-text focus-within:border-sky-500/50"
      >
        {selected.map((cc) => (
          <span
            key={cc}
            className="inline-flex items-center gap-1 rounded bg-sky-500/15 text-sky-100 px-1.5 py-0.5 text-[11.5px]"
          >
            <span className="text-[12px] leading-none">{flag(cc)}</span>
            <span className="tabular-nums">{cc}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(selected.filter((x) => x !== cc));
              }}
              className="hover:text-white"
              title="Remove"
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        <div className="flex-1 min-w-[120px] inline-flex items-center gap-1.5">
          {selected.length === 0 && <Search className="h-3 w-3 text-neutral-500 shrink-0" />}
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={
              selected.length === 0
                ? "Search countries…"
                : `Add another (${selected.length} selected)`
            }
            className="flex-1 bg-transparent outline-none text-neutral-100 placeholder:text-neutral-500 text-[12.5px] min-w-0"
          />
          {selected.length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange([]); }}
              className="text-[10px] uppercase tracking-wider text-neutral-500 hover:text-neutral-300 shrink-0"
              title="Clear all"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Dropdown — portal'd to body so no ancestor overflow clips it. */}
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{ top: pos.top, left: pos.left, width: pos.width }}
          className="fixed z-[1000] max-h-[300px] overflow-y-auto rounded-md border border-sky-900/40 bg-[#0a1224] shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2.5 text-[12px] text-neutral-500">No matches.</div>
          ) : (
            <ul>
              {filtered.map((c) => {
                const isActive = selected.includes(c.cc);
                return (
                  <li key={c.cc}>
                    <button
                      type="button"
                      onClick={() => toggle(c.cc)}
                      className={`w-full text-left px-3 py-1.5 text-[12.5px] flex items-center gap-2 transition-colors ${
                        isActive
                          ? "bg-sky-500/15 text-sky-100"
                          : "text-neutral-200 hover:bg-sky-500/10"
                      }`}
                    >
                      <span
                        className={`h-3.5 w-3.5 rounded-sm border flex items-center justify-center shrink-0 ${
                          isActive ? "bg-sky-500 border-sky-500" : "border-sky-900/40"
                        }`}
                      >
                        {isActive && <Check className="h-2 w-2 text-white" strokeWidth={4} />}
                      </span>
                      <span className="text-[13px] leading-none">{flag(c.cc)}</span>
                      <span className="flex-1 min-w-0 truncate">{c.name}</span>
                      <span className="tabular-nums text-[11px] text-neutral-500">{c.cc}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   CARD INTELLIGENCE TAB
   ────────────────────────────────────────────────────────────────────────── */

const NETWORK_LIST = ["VISA", "MASTERCARD", "AMEX", "DISCOVER", "JCB", "RUPAY", "MIR", "UNIONPAY", "DINERS", "MAESTRO"];
const CARD_TYPE_LIST = ["credit", "debit", "prepaid", "unknown"];

const DEFAULT_FILTERS = {
  bin: "",
  last4: "",
  networks: [],
  types: [],
  issuerQuery: "",
  countries: [],
  validity: "all", // all | valid | expired
  dateFrom: "",
  dateTo: "",
};

function CardIntelligenceTab() {
  // Two-state filter model: `filters` is what the user is editing in the
  // form (pending), `appliedFilters` is what actually drives the query.
  // Searches only fire when the user clicks "Apply filters" — every fetch
  // costs quota so we don't run on every keystroke.
  //
  // Initial state honors `?issuer=<name>` from the URL so deep links from
  // the Issuers tab (or shared URLs) pre-fill the issuer filter and run
  // the search on first render — no extra fetch round-trip.
  const initialFilters = useMemo(() => {
    if (typeof window === "undefined") return DEFAULT_FILTERS;
    const issuer = new URLSearchParams(window.location.search).get("issuer");
    return issuer ? { ...DEFAULT_FILTERS, issuerQuery: issuer } : DEFAULT_FILTERS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [filterOpen, setFilterOpen] = useState(true);
  // Sort keys map 1:1 to the backend's `sort` enum: `exposed | expiry |
  // issuer | log_id`. The BIN column header is intentionally not sortable
  // because the backend doesn't offer that sort.
  const [sort, setSort] = useState({ key: "exposed", dir: "desc" });
  // Backend caps the page size at 50; we don't expose a selector for it.
  const perPage = 50;
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [openCardId, setOpenCardId] = useState(null);
  // When on, the table renders every card as fully-masked dots — useful for
  // screenshots and demos where even BIN6 + last-four shouldn't be visible.
  const [maskAll, setMaskAll] = useState(false);

  // Server-side data: { rows, total_matching, page, page_size }.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 402 + upgrade_required → soft upsell instead of a generic error banner.
  const [upgradeRequired, setUpgradeRequired] = useState(false);

  const toggleArr = (key, val) => {
    setFilters((f) => {
      const arr = new Set(f[key]);
      if (arr.has(val)) arr.delete(val);
      else arr.add(val);
      return { ...f, [key]: [...arr] };
    });
    // No setPage(1) here — pending edits don't affect pagination. Page
    // resets to 1 inside applyFilters() when the user actually searches.
  };

  // Build the query string from APPLIED filters + sort + page. Pending
  // filter edits don't trigger fetches. Sort and page changes do (they're
  // "navigation on the current result set", not a new search).
  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (appliedFilters.bin)         p.set("bin", appliedFilters.bin);
    if (appliedFilters.last4)       p.set("last4", appliedFilters.last4);
    if (appliedFilters.issuerQuery) p.set("issuer", appliedFilters.issuerQuery);
    if (appliedFilters.networks.length)  p.set("networks", appliedFilters.networks.join(","));
    if (appliedFilters.types.length)     p.set("card_types", appliedFilters.types.join(","));
    if (appliedFilters.countries.length) p.set("countries", appliedFilters.countries.join(","));
    if (appliedFilters.validity === "valid")   p.set("validity", "valid_only");
    if (appliedFilters.validity === "expired") p.set("validity", "expired_only");
    if (appliedFilters.dateFrom) p.set("exposed_from", appliedFilters.dateFrom);
    if (appliedFilters.dateTo)   p.set("exposed_to",   appliedFilters.dateTo);
    p.set("page", String(page));
    p.set("page_size", String(perPage));
    p.set("sort", sort.key);
    p.set("sort_dir", sort.dir);
    return p.toString();
  }, [appliedFilters, page, perPage, sort]);

  // Has the user edited the form without applying yet?
  const hasPendingChanges = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(appliedFilters),
    [filters, appliedFilters],
  );

  // Mirror the issuer filter to the URL so refresh + share carry the
  // current filter context. Only `issuer` is persisted; other filters stay
  // in component state to keep the URL short.
  const syncIssuerToUrl = (issuer) => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (issuer && issuer.trim()) params.set("issuer", issuer.trim());
    else params.delete("issuer");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );
  };

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
    syncIssuerToUrl(filters.issuerQuery);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPage(1);
    syncIssuerToUrl("");
  };

  // Fetch fires whenever the applied query changes — i.e., on mount,
  // after Apply Filters, on sort, on page, on Reset. Each `queryString`
  // change is treated as one explicit user action; no debounce required.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUpgradeRequired(false);

    fetch(`${CARDS_URL}?${queryString}`, {
      headers: { Authorization: `Bearer ${getJwtToken()}` },
    })
      .then(async (r) => {
        if ([401, 419, 440].includes(r.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${r.status}`);
        }
        const json = await r.json().catch(() => ({}));
        if (r.status === 402 && json?.upgrade_required) throw { _upgrade: true };
        if (!r.ok) throw new Error(json?.message || `HTTP ${r.status}`);
        return json;
      })
      .then((json) => {
        if (cancelled) return;
        if (json?.success) setData(json);
        else setError(json?.message || "Failed to load cards.");
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?._upgrade) setUpgradeRequired(true);
        else {
          console.error("Cards fetch error:", e);
          setError(e?.message || "Network error");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [queryString]);

  // Adapt backend rows → existing JSX field names. The mapping is small:
  //   - synthesize a stable `id` from log_id + card_index
  //   - rename last4 → last_four, is_valid_expiry_now → is_valid_expiry,
  //     log_id → source_log_id
  //   - convert exposed_at from epoch seconds → ms (fmt helpers expect ms)
  const pageRows = useMemo(
    () => (data?.rows || []).map((r) => ({
      ...r,
      id: `${r.log_id}-${r.card_index}`,
      last_four: r.last4,
      is_valid_expiry: r.is_valid_expiry_now,
      source_log_id: r.log_id,
      exposed_at: (r.exposed_at || 0) * 1000,
    })),
    [data],
  );

  const totalMatching = data?.total_matching ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalMatching / perPage));
  const safePage = Math.min(page, totalPages);

  // Selection resets whenever the visible rows change. Server-side paging
  // means selection across pages isn't meaningful here.
  useEffect(() => { setSelected(new Set()); }, [filters, sort, page, perPage]);

  const allSelectedOnPage = pageRows.length > 0 && pageRows.every((r) => selected.has(r.id));
  const toggleSelectAllPage = () => {
    setSelected((sel) => {
      const next = new Set(sel);
      if (allSelectedOnPage) pageRows.forEach((r) => next.delete(r.id));
      else pageRows.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected((sel) => {
      const next = new Set(sel);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // CSV export operates on the currently visible page only — selection is
  // bounded to the page (no cross-page persistence), so this matches what
  // the user actually picked.
  const exportSelectionCsv = () => {
    const rows = pageRows.filter((c) => selected.has(c.id));
    if (!rows.length) return;
    const cols = [
      "bin6","last_four","expiry_month","expiry_year","is_valid_expiry",
      "card_network","card_type","issuer_bank","issuer_country",
      "exposed_at","source_log_id",
    ];
    const head = cols.join(",");
    const body = rows.map((r) => cols.map((k) => {
      const v = r[k];
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([head + "\n" + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payment-fraud-export-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sortHeader = (key, label) => {
    const active = sort.key === key;
    return (
      <button
        type="button"
        onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }))}
        className={`inline-flex items-center gap-1 hover:text-sky-300 transition-colors ${
          active ? "text-sky-200" : "text-neutral-300"
        }`}
      >
        {label}
        {active && (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </button>
    );
  };

  // Detail drawer pulls from the currently-loaded page. Closing on page
  // change is fine — the same row would reappear with a different page.
  const openCard = openCardId ? pageRows.find((c) => c.id === openCardId) : null;

  // Upgrade-required state takes over the whole tab — the table isn't
  // useful when the tier can't access it. Calm amber surface card with a
  // CTA to the upgrades page.
  if (upgradeRequired) {
    return <UpgradeRequiredCta />;
  }

  return (
    <div className="space-y-3">
      {/* Generic error banner — dismissible, doesn't block the table from
          rendering whatever was loaded before the failure. */}
      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-200 flex items-start justify-between gap-3">
          <span className="min-w-0">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-[11px] text-neutral-400 hover:text-white shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Filter bar */}
      <SurfaceCard noPad>
        <button
          type="button"
          onClick={() => setFilterOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 h-12 text-neutral-100 hover:bg-sky-500/5 transition-colors"
        >
          <span className="inline-flex items-center gap-2 text-[12px] font-semibold">
            <Filter className="h-3.5 w-3.5 text-sky-300/80" />
            <span className="uppercase tracking-wider text-[11px] text-neutral-300">Filters</span>
            <span className="text-[11px] font-normal text-neutral-500">
              ({fmtNum(totalMatching)} matching)
            </span>
          </span>
          {filterOpen ? <ChevronUp className="h-3.5 w-3.5 text-neutral-400" /> : <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />}
        </button>
        {filterOpen && (
          <div className="px-5 pb-4 border-t border-sky-900/30">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3 pt-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5 text-neutral-500">
                  BIN (partial OK)
                </div>
                <TextInput
                  value={filters.bin}
                  onChange={(v) => setFilters((f) => ({ ...f, bin: v.replace(/\D/g, "").slice(0, 8) }))}
                  placeholder="e.g. 421764"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5 text-neutral-500">
                  Last 4
                </div>
                <TextInput
                  value={filters.last4}
                  onChange={(v) => setFilters((f) => ({ ...f, last4: v.replace(/\D/g, "").slice(0, 4) }))}
                  placeholder="1234"
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5 text-neutral-500">
                  Issuer bank
                </div>
                <IssuerPicker
                  value={filters.issuerQuery}
                  onChange={(name) => setFilters((f) => ({ ...f, issuerQuery: name }))}
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5 text-neutral-500">
                  Validity
                </div>
                <div className="flex gap-1.5">
                  {[
                    { v: "all",     l: "All" },
                    { v: "valid",   l: "Valid only" },
                    { v: "expired", l: "Expired only" },
                  ].map((o) => (
                    <Chip
                      key={o.v}
                      active={filters.validity === o.v}
                      onClick={() => setFilters((f) => ({ ...f, validity: o.v }))}
                    >
                      {o.l}
                    </Chip>
                  ))}
                </div>
              </div>

              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider mb-1.5 text-neutral-500">
                  Network
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {NETWORK_LIST.map((n) => (
                    <Chip key={n} active={filters.networks.includes(n)} onClick={() => toggleArr("networks", n)}>
                      {n}
                    </Chip>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5 text-neutral-500">
                  Card type
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CARD_TYPE_LIST.map((t) => (
                    <Chip key={t} active={filters.types.includes(t)} onClick={() => toggleArr("types", t)}>
                      {t}
                    </Chip>
                  ))}
                </div>
              </div>

              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider mb-1.5 text-neutral-500">
                  Issuer country
                </div>
                <CountryMultiSelect
                  selected={filters.countries}
                  onChange={(arr) => setFilters((f) => ({ ...f, countries: arr }))}
                />
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5 text-neutral-500">
                  Exposed from
                </div>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                  className="w-full rounded text-[12px] py-1.5 px-2 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 focus:border-sky-500/50"
                  style={{ colorScheme: "dark" }}
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider mb-1.5 text-neutral-500">
                  Exposed to
                </div>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                  className="w-full rounded text-[12px] py-1.5 px-2 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 focus:border-sky-500/50"
                  style={{ colorScheme: "dark" }}
                />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <button
                type="button"
                onClick={resetFilters}
                className="text-[11.5px] text-neutral-400 hover:text-neutral-200"
              >
                Reset filters
              </button>
              <div className="flex items-center gap-3 flex-wrap">
                {hasPendingChanges && (
                  <span className="text-[11px] text-amber-300 inline-flex items-center gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                    Unapplied changes
                  </span>
                )}
                {/* Quota notice — placed next to the Apply button so the
                    user sees the cost the moment before they click. */}
                <span className="text-[11px] text-neutral-500 inline-flex items-center gap-1">
                  <Info className="h-3 w-3 text-sky-300/80" />
                  Counts toward your monthly quota.
                </span>
                <button
                  type="button"
                  onClick={applyFilters}
                  disabled={!hasPendingChanges}
                  className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border transition-colors ${
                    hasPendingChanges
                      ? "bg-sky-500/15 border-sky-500/40 text-sky-100 hover:bg-sky-500/25"
                      : "bg-[#0a1224] border-sky-900/40 text-neutral-500 cursor-not-allowed"
                  }`}
                  title={hasPendingChanges ? "Run search — uses 1 monthly quota" : "No pending changes"}
                >
                  <Search className="h-3.5 w-3.5" />
                  Apply filters
                </button>
              </div>
            </div>
          </div>
        )}
      </SurfaceCard>

      {/* Bulk action row */}
      <div className="flex items-center justify-between gap-3 px-1">
        <div className="text-[11.5px] text-neutral-400 inline-flex items-center gap-2 flex-wrap">
          {selected.size > 0
            ? <><span className="text-neutral-100 font-medium">{fmtNum(selected.size)}</span> selected</>
            : <>{fmtNum(totalMatching)} matching rows</>}
          {/* Browse-mode hint — the backend returns `mode: "browse"` when
              no real filter has been applied; rows are just the latest
              entries (no quota was spent). Make this obvious so users don't
              think they ran a search. */}
          {data?.mode === "browse" && (
            <span
              title="No filter applied — showing the latest entries without consuming quota."
              className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset bg-neutral-500/10 text-neutral-300 ring-neutral-500/30"
            >
              <Info className="h-3 w-3" />
              <span className="normal-case font-normal">Showing only latest added entries</span>
            </span>
          )}
          {loading && data && (
            <span className="inline-flex items-center gap-1 text-sky-300">
              <span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse" />
              Updating…
            </span>
          )}
          {/* Monthly search-quota indicator. Each filtered fetch consumes a
              slot; we show used / limit so the user sees burn rate at a
              glance. Color shifts amber when ≥80% used. */}
          {(() => {
            const q = data?.quota;
            if (!q || typeof q.monthly_limit !== "number") return null;
            const used = q.monthly_used ?? 0;
            const limit = q.monthly_limit;
            const pct = limit > 0 ? used / limit : 0;
            const cls =
              pct >= 0.95
                ? "bg-red-500/10 text-red-300 ring-red-500/30"
                : pct >= 0.8
                ? "bg-amber-500/10 text-amber-300 ring-amber-500/30"
                : "bg-sky-500/10 text-sky-300 ring-sky-500/30";
            return (
              <span
                title="Monthly search quota"
                className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset ${cls}`}
              >
                <span>Quota</span>
                <span className="tabular-nums font-mono normal-case">
                  {fmtNum(used)} / {fmtNum(limit)}
                </span>
              </span>
            );
          })()}
        </div>
        <div className="flex items-center gap-2">
          {/* Screenshot-safe mask toggle — hides BIN6 + last-four for every
              row in the table. Privacy controls live in the same row as the
              other bulk actions so they're easy to find. */}
          <button
            type="button"
            onClick={() => setMaskAll((v) => !v)}
            title={maskAll ? "Show BIN6 + last 4" : "Mask all card numbers"}
            className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded border transition-colors ${
              maskAll
                ? "bg-sky-500/15 border-sky-500/40 text-sky-100 hover:bg-sky-500/25"
                : "bg-transparent border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 hover:text-neutral-100"
            }`}
          >
            {maskAll ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {maskAll ? "Card numbers hidden" : "Mask all card numbers"}
          </button>
          <button
            type="button"
            disabled={!selected.size}
            onClick={exportSelectionCsv}
            className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded border transition-colors disabled:opacity-40 ${
              selected.size
                ? "bg-sky-500/15 border-sky-500/40 text-sky-100 hover:bg-sky-500/25"
                : "bg-transparent border-sky-900/40 text-neutral-400"
            }`}
          >
            <Download className="h-3.5 w-3.5" />
            Export selection (CSV)
          </button>
          <button
            type="button"
            disabled={!selected.size}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded border border-sky-900/40 text-neutral-200 hover:bg-sky-500/5 transition-colors disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
            Add to watchlist
          </button>
        </div>
      </div>

      {/* Table */}
      <SurfaceCard noPad className="relative">
        {/* Indeterminate progress bar — appears at the top of the card while
            a refetch is in flight and previous data is still on screen.
            Calm 2px sky pulse, sits over the rounded border so it visually
            reads as the card's "loading" affordance. */}
        {loading && data && (
          <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden z-20 rounded-t-md">
            <div className="absolute inset-0 bg-sky-500/70 animate-pulse" />
          </div>
        )}
        <div
          className={`overflow-x-auto transition-opacity ${loading && data ? "opacity-60" : "opacity-100"}`}
        >
          <table className="w-full text-[12px] border-collapse">
            <thead className="sticky top-0 z-10 bg-[#0b1426]/95 backdrop-blur">
              <tr className="text-neutral-300 border-b border-sky-900/30">
                <th className="text-left px-4 py-2.5 font-semibold uppercase tracking-wider text-[10.5px] w-8">
                  <input
                    type="checkbox"
                    checked={allSelectedOnPage}
                    onChange={toggleSelectAllPage}
                    className="accent-sky-500"
                  />
                </th>
                <th className="text-left px-2 py-2.5 font-semibold uppercase tracking-wider text-[10.5px] w-4"></th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">Card</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">Network</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">Type</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">{sortHeader("issuer", "Issuer")}</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">Country</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">{sortHeader("expiry", "Expiry")}</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">{sortHeader("exposed", "Exposed")}</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">{sortHeader("log_id", "Log ID")}</th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                // Initial-load skeleton — 8 placeholder rows so the table
                // height doesn't collapse before the first response arrives.
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`skel-${i}`} className="border-b border-sky-900/20">
                    {Array.from({ length: 10 }).map((__, j) => (
                      <td key={j} className="px-3 py-2.5">
                        <div className="h-2.5 bg-sky-900/30 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              )}
              {!loading && pageRows.length === 0 && (
                <tr><td colSpan={10} className="text-center px-4 py-10 text-[12px] text-neutral-500">No matching cards.</td></tr>
              )}
              {pageRows.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setOpenCardId(c.id)}
                  className={`cursor-pointer transition-colors border-b border-sky-900/20 ${
                    selected.has(c.id)
                      ? "bg-sky-500/10 hover:bg-sky-500/15"
                      : "hover:bg-sky-500/5"
                  }`}
                >
                  <td className="px-4 py-2 align-middle" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggleOne(c.id)}
                      className="accent-sky-500"
                    />
                  </td>
                  <td className="px-2 py-2 align-middle"><ValidityDot valid={c.is_valid_expiry} unknown={c.expiry_month == null || c.expiry_year == null} /></td>
                  <td className="px-3 py-2 align-middle"><MaskedCardText card={c} fullyMasked={maskAll} className="text-neutral-100" /></td>
                  <td className="px-3 py-2 align-middle"><NetworkBadge net={c.card_network} size="sm" /></td>
                  <td className="px-3 py-2 align-middle text-neutral-400">
                    <span className="capitalize">{c.card_type}</span>
                    {c.card_tier && (
                      <span className="ml-1.5 inline-flex items-center text-[9.5px] font-medium uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset bg-sky-500/10 text-sky-300 ring-sky-500/30">
                        {c.card_tier}
                      </span>
                    )}
                  </td>
                  <td className={`px-3 py-2 align-middle truncate max-w-[220px] ${c.issuer_bank ? "text-neutral-100" : "text-neutral-500"}`} title={c.issuer_bank || "Unknown issuer"}>{c.issuer_bank || "—"}</td>
                  <td className="px-3 py-2 align-middle"><Country cc={c.issuer_country} /></td>
                  <td className={`px-3 py-2 align-middle tabular-nums ${
                    c.expiry_month == null || c.expiry_year == null
                      ? "text-neutral-500"
                      : c.is_valid_expiry
                      ? "text-neutral-100"
                      : "text-neutral-500 line-through"
                  }`}>
                    {fmtExpiry(c.expiry_month, c.expiry_year)}
                  </td>
                  <td className="px-3 py-2 align-middle tabular-nums text-neutral-400" title={fmtAbsolute(c.exposed_at)}>{fmtRelative(c.exposed_at)}</td>
                  <td className="px-3 py-2 align-middle font-mono tabular-nums text-[11px] text-neutral-500">{c.source_log_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-sky-900/30 text-[12px] text-neutral-400">
          <div className="tabular-nums">
            {totalMatching === 0
              ? "0"
              : `${(safePage - 1) * perPage + 1}–${Math.min(safePage * perPage, totalMatching)} of ${fmtNum(totalMatching)}`}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="px-2 py-1 rounded text-[12px] border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              Prev
            </button>
            <div className="px-2 tabular-nums text-neutral-100">{safePage} / {totalPages}</div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="px-2 py-1 rounded text-[12px] border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              Next
            </button>
          </div>
        </div>
      </SurfaceCard>

      {/* Detail drawer */}
      {openCard && <CardDetailDrawer card={openCard} onClose={() => setOpenCardId(null)} />}
    </div>
  );
}

function CardDetailDrawer({ card, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const copyLogId = () => {
    try { navigator.clipboard.writeText(String(card.source_log_id)); } catch {}
  };

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 transition-opacity" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-3xl flex flex-col border-l border-sky-900/40 bg-[#06101e] shadow-[0_0_50px_rgba(0,0,0,0.5)] transform transition-transform duration-300 translate-x-0">
        {/* Header — matches DetailSidebar: eyebrow with icon on top, big title
            below, action buttons on the right. */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-sky-900/30 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[13px] text-neutral-400">
              <CreditCard className="h-4 w-4" />
              <span>Card Detail</span>
            </div>
            <h2 className="mt-1 text-[16px] font-semibold tracking-tight text-neutral-100 font-mono tabular-nums">
              {card.bin8}<span className="mx-1 text-neutral-600">••••</span>{card.last_four}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <PfIconBtn title="Copy log ID" onClick={copyLogId}>
              <Copy className="h-4 w-4" />
            </PfIconBtn>
            <PfIconBtn title="Open log" onClick={() => {}}>
              <ExternalLink className="h-4 w-4" />
            </PfIconBtn>
            <PfIconBtn title="Close" onClick={onClose}>
              <X className="h-4 w-4" />
            </PfIconBtn>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-5 space-y-6">
          {/* Status row — network + validity + relative time, RF detail-pane style. */}
          <div className="flex items-center gap-3 flex-wrap">
            <NetworkBadge net={card.card_network} />
            <span className="inline-flex items-center gap-1.5 text-[12px] text-neutral-300">
              {(() => {
                const expiryUnknown = card.expiry_month == null || card.expiry_year == null;
                return (
                  <>
                    <ValidityDot valid={card.is_valid_expiry} unknown={expiryUnknown} />
                    {expiryUnknown
                      ? "Expiry unknown"
                      : card.is_valid_expiry
                      ? "Currently valid"
                      : "Expired"}
                  </>
                );
              })()}
            </span>
            <span className="text-[12px] text-neutral-500" title={fmtAbsolute(card.exposed_at)}>
              Exposed {fmtRelative(card.exposed_at)}
            </span>
          </div>

          <PfSection title="Card">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-[12.5px]">
              <Field label="BIN6"     value={<span className="font-mono tabular-nums">{card.bin6}</span>} />
              <Field label="BIN8"     value={card.bin8 ? <span className="font-mono tabular-nums">{card.bin8}</span> : "—"} />
              <Field label="Last 4"   value={<span className="font-mono tabular-nums">{card.last_four}</span>} />
              <Field label="Expiry"   value={(() => {
                const unknown = card.expiry_month == null || card.expiry_year == null;
                return (
                  <span className={`font-mono tabular-nums ${
                    unknown ? "text-neutral-500" : card.is_valid_expiry ? "" : "line-through"
                  }`}>{fmtExpiry(card.expiry_month, card.expiry_year)}</span>
                );
              })()} />
              <Field label="Network"  value={card.card_network === "UNKNOWN" ? "Unknown" : card.card_network} />
              <Field label="Brand"    value={card.card_brand || "—"} />
              <Field label="Type"     value={<span className="capitalize">{card.card_type}</span>} />
              <Field label="Tier"     value={card.card_tier ? <span className="capitalize">{card.card_tier}</span> : "—"} />
              <Field label="Source"   value={<span className="font-mono tabular-nums">#{card.source_log_id}</span>} />
            </dl>
          </PfSection>

          <PfSection title="Issuer">
            <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 p-3 flex items-center gap-3">
              <div className="h-9 w-9 rounded flex items-center justify-center bg-[#000414] border border-sky-900/40">
                <Building2 className="h-4 w-4 text-sky-300/80" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] truncate text-neutral-100">{card.issuer_bank}</div>
                <div className="text-[11.5px] mt-0.5 flex items-center gap-2 text-neutral-400">
                  <Country cc={card.issuer_country} />
                  <span>·</span>
                  <span>{card.card_network}</span>
                  <span>·</span>
                  <span>{card.card_type}</span>
                </div>
              </div>
            </div>
          </PfSection>


        </div>
      </aside>
    </div>
  );
}

// Match the IconBtn pattern used by DetailSidebar / DetailSidebarEvents.
function PfIconBtn({ children, title, onClick, disabled }) {
  return (
    <button
      className={`p-2 rounded text-neutral-300 ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5 hover:text-white"}`}
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

function PfSection({ title, children }) {
  return (
    <section>
      <h3 className="mb-3 text-[13px] font-semibold tracking-wide text-neutral-400 uppercase">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <dt className="text-[10.5px] uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className="mt-0.5 text-neutral-100">{value}</dd>
    </div>
  );
}

/* Shared upgrade-required CTA — mirrors the Brand Protection gate pattern
   (centered frosted card, round sky icon chip). Reframed as the
   "Payment Fraud Intelligence" add-on so it reads correctly for any tier
   that doesn't have the module enabled yet. */
function UpgradeRequiredCta() {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto mt-12">
      <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)] p-8 text-center">
        <div className="mx-auto inline-flex rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-4">
          <CreditCard className="h-6 w-6 text-sky-300" />
        </div>
        <h2 className="text-xl font-semibold text-white">
          Payment Fraud Intelligence is an add-on module
        </h2>
        <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
          Compromised-card monitoring, issuer leaderboards, and the card
          information table are delivered as an add-on. Yearly{" "}
          <span className="text-sky-200 font-medium">Threat Intelligence</span>{" "}
          or <span className="text-sky-200 font-medium">Enterprise</span>{" "}
          license is required.
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/upgrades?tab=addons")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
          >
            View add-ons
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-lg border border-sky-900/40 px-4 py-2 text-sm text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   ALERTS TAB
   ────────────────────────────────────────────────────────────────────────── */

function AlertsTab({ cards }) {
  const [entries, setEntries] = useState([]);
  // Quota carries credit-based pricing:
  //   { credits_used, credits_limit, credits_remaining,
  //     cost_per_bin, cost_per_issuer }
  const [quota, setQuota] = useState(null);
  // Per-type counts from the list endpoint so we can render the
  // issuer/BIN breakdown next to the quota strip.
  //   { total, active, paused, bin_total, bin_active,
  //     issuer_total, issuer_active }
  const [counts, setCounts] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [view, setView] = useState("rules"); // rules | timeline | resolved
  const [createOpen, setCreateOpen] = useState(false);

  const fetchEntries = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(LIST_CARD_WATCHLIST_URL, {
        headers: { Authorization: `Bearer ${getJwtToken()}` },
      });
      if (res.status === 401) { redirectToLogin(); return; }
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.error || payload?.success === false) {
        throw new Error(formatApiError(payload, `Could not load watchlist (${res.status}).`));
      }
      setEntries(Array.isArray(payload.entries) ? payload.entries : []);
      setQuota(payload.quota || null);
      setCounts(payload.counts || null);
      setTotal(
        Number.isFinite(payload.total)
          ? payload.total
          : (payload.counts?.total ?? payload.entries?.length ?? 0)
      );
    } catch (err) {
      setError(err?.message || "Could not load watchlist.");
      setEntries([]);
      setQuota(null);
      setCounts(null);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, []);

  // In-flight mutation tracker + per-entry transient error message. Keyed
  // by entry id so each card can show its own spinner / error without
  // blocking the rest of the list.
  const [pendingId, setPendingId] = useState(null);
  const [errorByEntry, setErrorByEntry] = useState({});
  // Inline delete confirmation — when set, the matching card's footer
  // flips to a "Delete this rule?" strip instead of the normal actions.
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  // Which entry the edit modal is currently editing (null = closed).
  const [editingEntry, setEditingEntry] = useState(null);

  // Triggered-alerts timeline state — only fetched when the user
  // switches to the timeline view (lazy).
  const [alerts, setAlerts] = useState([]);
  const [alertsStats, setAlertsStats] = useState(null);
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState("");
  const [deliveredOnly, setDeliveredOnly] = useState(false);
  const [watchlistFilter, setWatchlistFilter] = useState("");   // "" = all
  const alertsPageSize = 20;

  // Detail-sidebar state — null when closed. We store the id (not the
  // row) so the sidebar refetches even when the timeline list changes.
  const [openAlertId, setOpenAlertId] = useState(null);

  // User profile + subscription tier — drives the email recipient picker
  // inside the create/edit modals. Same pattern as the Watchlists
  // settings card: primary notify is always the current user's email,
  // secondary picks from org members (Enterprise / Threat Intel only).
  const [userEmail, setUserEmail] = useState("");
  const [hasOrg, setHasOrg] = useState(null);          // null = still loading
  const [subscription, setSubscription] = useState(null);
  const multiNotifyAllowed = subscription === "tifirm" || subscription === "enterprise";

  useEffect(() => {
    const jwt = getJwtToken();
    if (!jwt) return;
    let cancelled = false;
    (async () => {
      try {
        const [userRes, subRes] = await Promise.all([
          fetch("https://whiteintel.io/api/regular/app/v2/user/get_user_information.php", {
            headers: { Authorization: `Bearer ${jwt}` },
          }),
          fetch("https://whiteintel.io/api/regular/app/v2/user/get_subscription.php", {
            headers: { Authorization: `Bearer ${jwt}` },
          }),
        ]);
        if (cancelled) return;
        if (userRes.status === 401 || subRes.status === 401) {
          redirectToLogin();
          return;
        }
        const userData = await userRes.json().catch(() => ({}));
        const subData = await subRes.json().catch(() => ({}));
        if (cancelled) return;
        setUserEmail(userData?.user?.email || "");
        setHasOrg(Boolean(userData?.user?.organization_name));
        setSubscription(subData?.subscription || null);
      } catch {
        // Non-fatal — modal will fall back to "your email" placeholder
        // and hide the secondary recipient picker.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    setAlertsError("");
    const body = {
      page: alertsPage,
      page_size: alertsPageSize,
      ...(deliveredOnly ? { delivered_only: true } : {}),
      ...(watchlistFilter ? { watchlist_id: Number(watchlistFilter) } : {}),
    };
    try {
      const res = await fetch(LIST_CARD_ALERTS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401) { redirectToLogin(); return; }
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.success === false || payload?.error) {
        throw new Error(formatApiError(payload, `Could not load alerts (${res.status}).`));
      }
      setAlerts(Array.isArray(payload.alerts) ? payload.alerts : []);
      setAlertsStats(payload.stats || null);
    } catch (err) {
      setAlertsError(err?.message || "Could not load alerts.");
      setAlerts([]);
      setAlertsStats(null);
    } finally {
      setAlertsLoading(false);
    }
  };

  // Refetch whenever the user is on the timeline view and any filter
  // (or page) changes. The rules-view doesn't need this data.
  useEffect(() => {
    if (view !== "timeline") return;
    fetchAlerts();
  }, [view, alertsPage, deliveredOnly, watchlistFilter]);

  // Reset to page 1 whenever a filter flips (otherwise we might end up
  // on a page that no longer exists after filtering).
  useEffect(() => {
    setAlertsPage(1);
  }, [deliveredOnly, watchlistFilter]);

  const mutateEntry = async (id, action) => {
    // action: "pause" | "resume" | undefined (delete)
    if (pendingId) return;
    setPendingId(id);
    setErrorByEntry((m) => { const { [id]: _, ...rest } = m; return rest; });

    const body = { id, ...(action ? { action } : {}) };
    try {
      const res = await fetch(DELETE_CARD_WATCHLIST_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401) { redirectToLogin(); return; }
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.success === false || payload?.error) {
        throw new Error(formatApiError(payload, `Request failed (${res.status}).`));
      }
      if (!action) {
        // Delete — drop from local state and refetch so quota reflects.
        setEntries((es) => es.filter((e) => e.id !== id));
        fetchEntries();
      } else {
        // Pause / resume — flip is_active locally; the rest of the row
        // (counters, timestamps) stays untouched until the next refetch.
        setEntries((es) => es.map((e) =>
          e.id === id ? { ...e, is_active: action === "resume" } : e
        ));
      }
    } catch (err) {
      setErrorByEntry((m) => ({ ...m, [id]: err?.message || "Request failed." }));
    } finally {
      setPendingId(null);
      setConfirmDeleteId((c) => (c === id ? null : c));
    }
  };

  const onToggleActive = (entry) => {
    mutateEntry(entry.id, entry.is_active ? "pause" : "resume");
  };
  const requestDelete = (id) => {
    setConfirmDeleteId(id);
    setErrorByEntry((m) => { const { [id]: _, ...rest } = m; return rest; });
  };
  const cancelDelete = () => setConfirmDeleteId(null);
  const confirmDelete = (id) => mutateEntry(id, undefined);

  // After a successful create the modal calls this; we refetch so the
  // new row, quota, and total all reflect the server state.
  const onCreated = () => {
    setCreateOpen(false);
    fetchEntries();
  };
  // After a successful edit the modal hands back the server-returned
  // entry. Merge it into local state (no full refetch needed — neither
  // quota nor the entry set changes, only the in-place fields).
  const onSaved = (updated) => {
    if (updated && Number.isFinite(updated.id)) {
      setEntries((es) => es.map((e) => e.id === updated.id ? { ...e, ...updated } : e));
    }
    setEditingEntry(null);
  };

  // Display helpers — keep the formatting consistent with how the
  // create modal renders the same fields.
  const labelByType = { bin: "BIN", issuer: "Issuer" };
  const criteriaTextOf = (e) => `${labelByType[e.entry_type] || e.entry_type} = ${e.entry_value}`;
  const tsMs = (s) => {
    if (!s) return null;
    const ms = new Date(String(s).replace(" ", "T")).getTime();
    return Number.isFinite(ms) ? ms : null;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded overflow-hidden border border-sky-900/40 bg-[#0a1224]">
          {[
            { v: "rules",    l: "Active rules" },
            { v: "timeline", l: "Triggered alerts" },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setView(o.v)}
              className={`text-[12px] px-3 py-1.5 transition-colors ${
                view === o.v
                  ? "bg-sky-500/15 text-sky-100"
                  : "text-neutral-400 hover:bg-sky-500/5 hover:text-neutral-200"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>

        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded border border-sky-500/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25 transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Create new alert rule
        </button>
      </div>

      {view === "rules" && (
        <>
          {/* Quota strip — surfaces credit-based usage + per-type cost
              + a counts breakdown of how those credits are allocated
              between BIN and issuer entries. */}
          {quota && (
            <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/60 px-3 py-2 text-[11.5px]">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] uppercase tracking-[0.1em] font-semibold text-neutral-500">
                  Watchlist quota
                </span>
                <span className="font-mono tabular-nums text-neutral-200">
                  {(quota.credits_used ?? 0).toLocaleString()} / {(quota.credits_limit ?? 0).toLocaleString()}
                </span>
                <span className="text-neutral-600">credits</span>
                <span className="text-neutral-600">·</span>
                <span className="text-neutral-400 tabular-nums">
                  {(quota.credits_remaining ?? 0).toLocaleString()} remaining
                </span>
                {counts && (
                  <span className="ml-auto text-neutral-500">
                    {counts.issuer_total ?? 0} issuer{(counts.issuer_total ?? 0) === 1 ? "" : "s"}
                    {" · "}
                    {counts.bin_total ?? 0} BIN{(counts.bin_total ?? 0) === 1 ? "" : "s"}
                    {(counts.paused ?? 0) > 0 && (
                      <span className="text-neutral-600"> · {counts.paused} paused</span>
                    )}
                  </span>
                )}
              </div>
              {(Number.isFinite(quota.cost_per_bin) || Number.isFinite(quota.cost_per_issuer)) && (
                <div className="mt-1 text-[10.5px] text-neutral-500">
                  {Number.isFinite(quota.cost_per_bin) && (
                    <>
                      <span className="text-neutral-400">{quota.cost_per_bin}</span> credit{quota.cost_per_bin === 1 ? "" : "s"} per BIN
                    </>
                  )}
                  {Number.isFinite(quota.cost_per_bin) && Number.isFinite(quota.cost_per_issuer) && (
                    <span className="text-neutral-600"> · </span>
                  )}
                  {Number.isFinite(quota.cost_per_issuer) && (
                    <>
                      <span className="text-neutral-400">{quota.cost_per_issuer}</span> credit{quota.cost_per_issuer === 1 ? "" : "s"} per issuer
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {loading ? (
            <SurfaceCard className="text-center py-12">
              <div className="inline-flex items-center gap-2 text-[12px] text-neutral-500">
                <span className="inline-block h-3 w-3 rounded-full border-2 border-sky-300 border-t-transparent animate-spin" />
                Loading watchlist…
              </div>
            </SurfaceCard>
          ) : error ? (
            <SurfaceCard className="text-center py-8">
              <div className="text-[12px] text-rose-300 mb-2">{error}</div>
              <button
                onClick={fetchEntries}
                className="text-[11.5px] px-3 py-1 rounded border border-sky-900/40 text-neutral-200 hover:bg-sky-500/5"
              >
                Retry
              </button>
            </SurfaceCard>
          ) : entries.length === 0 ? (
            <SurfaceCard className="text-center py-12">
              <div className="text-[12px] text-neutral-500">
                No watchlist rules yet. Click <span className="text-neutral-300 font-medium">Create new alert rule</span> to add one.
              </div>
            </SurfaceCard>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {entries.map((e) => {
                const title = e.label || criteriaTextOf(e);
                const subtitle = e.label ? criteriaTextOf(e) : null;
                const lastAlertMs = tsMs(e.last_alert_at);
                const typeChipClass = e.entry_type === "issuer"
                  ? "text-sky-300 ring-sky-500/40 bg-sky-500/10"
                  : "text-neutral-300 ring-neutral-700 bg-neutral-800/60";
                const isPending = pendingId === e.id;
                const isConfirmingDelete = confirmDeleteId === e.id;
                const entryError = errorByEntry[e.id];
                return (
                  <SurfaceCard key={e.id} className={isPending ? "opacity-80" : ""}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`inline-flex items-center text-[9.5px] uppercase tracking-[0.1em] font-semibold rounded px-1.5 py-0.5 ring-1 ring-inset ${typeChipClass}`}>
                            {labelByType[e.entry_type] || e.entry_type}
                          </span>
                          {!e.is_active && (
                            <span className="inline-flex items-center text-[9.5px] uppercase tracking-[0.1em] font-semibold rounded px-1.5 py-0.5 ring-1 ring-inset ring-neutral-700 bg-neutral-800/60 text-neutral-400">
                              Paused
                            </span>
                          )}
                        </div>
                        <h3 className="mt-2 text-[14px] font-semibold tracking-tight text-neutral-100 break-words">
                          {title}
                        </h3>
                        {subtitle && (
                          <div className="mt-0.5 text-[11.5px] font-mono text-neutral-400 break-all">
                            {subtitle}
                          </div>
                        )}
                      </div>
                      {isPending ? (
                        <span className="inline-block h-3 w-3 rounded-full border-2 border-sky-300 border-t-transparent animate-spin mt-1" />
                      ) : (
                        <Toggle checked={!!e.is_active} onChange={() => onToggleActive(e)} />
                      )}
                    </div>

                    {/* Metrics row */}
                    <div className="mt-3 flex items-baseline gap-4 flex-wrap text-[11.5px]">
                      <div>
                        <span className="text-neutral-100 font-semibold tabular-nums">{e.total_alerts ?? 0}</span>{" "}
                        <span className="text-neutral-500">triggers</span>
                      </div>
                      <div>
                        <span className="text-neutral-100 font-semibold tabular-nums">{e.total_cards_matched ?? 0}</span>{" "}
                        <span className="text-neutral-500">cards matched</span>
                      </div>
                      {lastAlertMs && (
                        <div className="text-neutral-500 ml-auto">
                          Last alert {fmtRelative(lastAlertMs)}
                        </div>
                      )}
                    </div>

                    {entryError && !isConfirmingDelete && (
                      <div className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 px-2.5 py-1.5 text-[11px] text-rose-300 whitespace-pre-line">
                        {entryError}
                      </div>
                    )}

                    {isConfirmingDelete ? (
                      <div className="mt-4 pt-3 border-t border-sky-900/30">
                        <div className="text-[11.5px] text-neutral-300 mb-2">
                          Delete this rule? Triggered-alert history is preserved.
                        </div>
                        {entryError && (
                          <div className="mb-2 text-[11px] text-rose-300 whitespace-pre-line">{entryError}</div>
                        )}
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={cancelDelete}
                            disabled={isPending}
                            className="text-[11.5px] font-medium px-2.5 py-1 rounded border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={() => confirmDelete(e.id)}
                            disabled={isPending}
                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded border border-rose-500/40 bg-rose-500/15 text-rose-200 hover:bg-rose-500/25 disabled:opacity-50"
                          >
                            {isPending && (
                              <span className="inline-block h-2.5 w-2.5 rounded-full border-2 border-rose-300 border-t-transparent animate-spin" />
                            )}
                            Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 pt-3 border-t border-sky-900/30 flex items-center justify-between gap-2">
                        <div className="text-[10.5px] text-neutral-500 min-w-0 truncate" title={[e.notify_email, e.secondary_notification_email].filter(Boolean).join(", ")}>
                          {e.notify_email || "—"}
                          {e.secondary_notification_email && (
                            <span className="text-neutral-600"> · +1</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            className="p-1.5 rounded text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/5 disabled:opacity-50"
                            title="Edit"
                            disabled={isPending}
                            onClick={() => setEditingEntry(e)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            className="p-1.5 rounded text-neutral-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                            title="Delete"
                            disabled={isPending}
                            onClick={() => requestDelete(e.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </SurfaceCard>
                );
              })}
            </div>
          )}
        </>
      )}

      {view === "timeline" && (() => {
        const stats = alertsStats || {};
        const total = Number(stats.total) || 0;
        const totalPages = Math.max(1, Math.ceil(total / alertsPageSize));
        const pageStart = total === 0 ? 0 : (alertsPage - 1) * alertsPageSize + 1;
        const pageEnd = Math.min(alertsPage * alertsPageSize, total);
        // Pre-compute the watchlist filter dropdown options from the
        // entries list we already loaded for the rules tab.
        const watchlistOptions = entries.map((e) => ({
          v: String(e.id),
          l: e.label || `${labelByType[e.entry_type] || e.entry_type} ${e.entry_value}`,
        }));
        return (
          <div className="space-y-4">
            {/* KPI strip */}
            {alertsStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard label="Total alerts" value={alertsStats.total ?? 0} />
                <KpiCard label="Delivered"    value={alertsStats.delivered_count ?? 0} />
                <KpiCard
                  label="Failed"
                  value={
                    (alertsStats.failed_count ?? 0) > 0
                      ? <span className="text-rose-300">{alertsStats.failed_count}</span>
                      : (alertsStats.failed_count ?? 0)
                  }
                  sub={(alertsStats.failed_count ?? 0) > 0 ? "needs review" : null}
                />
                <KpiCard label="Cards matched" value={alertsStats.total_cards_matched ?? 0} />
              </div>
            )}

            {/* Filter row */}
            <div className="flex items-center gap-2 flex-wrap">
              <Chip
                active={deliveredOnly}
                onClick={() => setDeliveredOnly((d) => !d)}
              >
                Delivered only
              </Chip>
              {watchlistOptions.length > 0 && (
                <select
                  value={watchlistFilter}
                  onChange={(e) => setWatchlistFilter(e.target.value)}
                  className="h-7 px-2 text-[12px] rounded border border-sky-900/40 bg-[#0a1224] text-neutral-200 hover:border-sky-700/60 focus:outline-none focus:border-sky-500/50"
                >
                  <option value="">All rules</option>
                  {watchlistOptions.map((o) => (
                    <option key={o.v} value={o.v}>{o.l}</option>
                  ))}
                </select>
              )}
              {(deliveredOnly || watchlistFilter) && (
                <button
                  type="button"
                  onClick={() => { setDeliveredOnly(false); setWatchlistFilter(""); }}
                  className="text-[11.5px] text-neutral-400 hover:text-neutral-200 underline-offset-2 hover:underline"
                >
                  Clear filters
                </button>
              )}
              <button
                type="button"
                onClick={fetchAlerts}
                disabled={alertsLoading}
                className="ml-auto text-[11.5px] text-neutral-400 hover:text-neutral-200 disabled:opacity-50"
                title="Refresh"
              >
                Refresh
              </button>
            </div>

            {/* Alerts list */}
            {alertsLoading ? (
              <SurfaceCard className="text-center py-12">
                <div className="inline-flex items-center gap-2 text-[12px] text-neutral-500">
                  <span className="inline-block h-3 w-3 rounded-full border-2 border-sky-300 border-t-transparent animate-spin" />
                  Loading alerts…
                </div>
              </SurfaceCard>
            ) : alertsError ? (
              <SurfaceCard className="text-center py-8">
                <div className="text-[12px] text-rose-300 mb-2 whitespace-pre-line">{alertsError}</div>
                <button
                  onClick={fetchAlerts}
                  className="text-[11.5px] px-3 py-1 rounded border border-sky-900/40 text-neutral-200 hover:bg-sky-500/5"
                >
                  Retry
                </button>
              </SurfaceCard>
            ) : alerts.length === 0 ? (
              <SurfaceCard className="text-center py-12">
                <div className="text-[12px] text-neutral-500">
                  No alerts match these filters.
                </div>
              </SurfaceCard>
            ) : (
              <SurfaceCard noPad>
                <ul className="divide-y divide-sky-900/30">
                  {alerts.map((a) => {
                    const w = a.watchlist || {};
                    const wTitle = w.label || `${labelByType[w.entry_type] || w.entry_type || "—"} = ${w.entry_value ?? "—"}`;
                    const wSubtitle = w.label
                      ? `${labelByType[w.entry_type] || w.entry_type || "—"} = ${w.entry_value ?? "—"}`
                      : null;
                    const sentMs = Number.isFinite(a.sent_at) ? a.sent_at * 1000 : null;
                    return (
                      <li
                        key={a.id}
                        onClick={() => setOpenAlertId(a.id)}
                        className="px-5 py-4 hover:bg-sky-500/[0.03] cursor-pointer transition-colors group"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ring-1 ring-inset ${
                            a.delivered
                              ? "bg-sky-500/15 ring-sky-500/40"
                              : "bg-rose-500/15 ring-rose-500/40"
                          }`}>
                            <Bell className={`h-3.5 w-3.5 ${a.delivered ? "text-sky-300" : "text-rose-300"}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            {/* Title row — name + status pill, with the
                                metric on the right so the eye lands on the
                                fired-rule + size-of-fire in one scan. */}
                            <div className="flex items-baseline gap-2 flex-wrap">
                              <span className="text-[13px] text-neutral-100 font-semibold break-words">
                                {wTitle}
                              </span>
                              <span className={`inline-flex items-center text-[9.5px] uppercase tracking-[0.1em] font-semibold rounded px-1.5 py-0.5 ring-1 ring-inset ${
                                a.delivered
                                  ? "text-sky-300 ring-sky-500/40 bg-sky-500/10"
                                  : "text-rose-300 ring-rose-500/40 bg-rose-500/10"
                              }`}>
                                {a.delivered ? "Delivered" : "Failed"}
                              </span>
                              <span className="ml-auto text-[11.5px] text-neutral-500 tabular-nums">
                                <span className="text-neutral-100 font-semibold">{a.cards_matched ?? 0}</span>{" "}
                                cards
                              </span>
                            </div>

                            {/* Mono criteria on its own line, so it's
                                scannable next to other rows. */}
                            {wSubtitle && (
                              <div className="mt-0.5 text-[11.5px] font-mono text-neutral-500 break-all">
                                {wSubtitle}
                              </div>
                            )}

                            {/* Failure detail — one-line preview only;
                                full message lives in the sidebar. */}
                            {!a.delivered && a.error_message && (
                              <div className="mt-1.5 text-[11px] text-rose-300 truncate" title={a.error_message}>
                                {a.error_message}
                              </div>
                            )}

                            {/* Footer — relative + absolute time on the
                                left, View-details affordance on the right. */}
                            <div className="mt-2 flex items-center gap-2 flex-wrap text-[10.5px] text-neutral-500">
                              {sentMs && (
                                <span title={fmtAbsolute(sentMs)}>
                                  {fmtRelative(sentMs)} · {fmtAbsolute(sentMs)}
                                </span>
                              )}
                              <span
                                className="ml-auto inline-flex items-center gap-1 text-[11px] text-sky-300/80 group-hover:text-sky-200"
                                aria-hidden
                              >
                                View details <ExternalLink className="h-3 w-3" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </SurfaceCard>
            )}

            {/* Pagination */}
            {!alertsLoading && !alertsError && total > 0 && (
              <div className="flex items-center justify-between gap-3 flex-wrap text-[11.5px]">
                <div className="text-neutral-500">
                  Showing <span className="text-neutral-200 tabular-nums">{pageStart}–{pageEnd}</span> of{" "}
                  <span className="text-neutral-200 tabular-nums">{total}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    disabled={alertsPage <= 1}
                    onClick={() => setAlertsPage((p) => Math.max(1, p - 1))}
                    className="px-2.5 py-1 rounded border border-sky-900/40 text-neutral-200 hover:bg-sky-500/5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  <div className="px-2.5 py-1 text-neutral-400">
                    Page <span className="text-neutral-200 tabular-nums">{alertsPage}</span> / <span className="tabular-nums">{totalPages}</span>
                  </div>
                  <button
                    type="button"
                    disabled={alertsPage >= totalPages}
                    onClick={() => setAlertsPage((p) => p + 1)}
                    className="px-2.5 py-1 rounded border border-sky-900/40 text-neutral-200 hover:bg-sky-500/5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {createOpen && (
        <CreateAlertModal
          onClose={() => setCreateOpen(false)}
          onCreate={onCreated}
          userEmail={userEmail}
          hasOrg={hasOrg}
          multiNotifyAllowed={multiNotifyAllowed}
        />
      )}
      {editingEntry && (
        <EditAlertModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={onSaved}
          userEmail={userEmail}
          hasOrg={hasOrg}
          multiNotifyAllowed={multiNotifyAllowed}
        />
      )}
      <AlertDetailSidebar
        open={openAlertId != null}
        alertId={openAlertId}
        onClose={() => setOpenAlertId(null)}
      />
    </div>
  );
}

function CreateAlertModal({ onClose, onCreate, userEmail, hasOrg, multiNotifyAllowed }) {
  // Form state matches the /paymentfraud/add_card_watchlist.php contract:
  //   { entry_type, entry_value, secondary_notification_email?, label? }
  // Only `bin` and `issuer` entry types are supported by the backend today.
  const [criteriaType, setCriteriaType] = useState("bin"); // bin | issuer
  const [criteriaValue, setCriteriaValue] = useState("");
  const [label, setLabel] = useState("");
  // Secondary recipient — picked from org members via OrgMemberPicker.
  // `showSecondary` tracks whether the picker is mounted (revealed via
  // "Add another recipient"); the email itself lives in `notifyEmail`.
  const [notifyEmail, setNotifyEmail] = useState("");
  const [showSecondary, setShowSecondary] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  const trimmedValue = criteriaValue.trim();
  const trimmedEmail = notifyEmail.trim();
  const emailOk = !trimmedEmail || isValidEmail(trimmedEmail);
  const canSubmit = !!trimmedValue && emailOk && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");

    const body = {
      entry_type: criteriaType,
      entry_value: trimmedValue,
      ...(trimmedEmail ? { secondary_notification_email: trimmedEmail } : {}),
      ...(label.trim() ? { label: label.trim() } : {}),
    };

    try {
      const res = await fetch(ADD_CARD_WATCHLIST_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401) { redirectToLogin(); return; }
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.success === false || payload?.error || payload?.status === "error") {
        throw new Error(formatApiError(payload, `Could not create watchlist entry (${res.status}).`));
      }

      // Optimistic local-state update so the new rule shows up immediately
      // without a refetch. Display name falls back to the criteria text
      // when the user didn't supply a label.
      const map = { bin: "BIN", issuer: "Issuer" };
      const criteriaText = `${map[criteriaType]} = ${trimmedValue}`;
      onCreate({
        name: label.trim() || criteriaText,
        criteria: criteriaText,
        active: true,
        ...(trimmedEmail ? { secondaryEmail: trimmedEmail } : {}),
      });
    } catch (err) {
      setError(err?.message || "Could not create watchlist entry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={submitting ? undefined : onClose} />
      <div className="relative w-full max-w-md rounded-md bg-[#0a1224] border border-sky-900/40 p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-sky-300/80">
              New rule
            </div>
            <h3 className="mt-1 text-[15px] font-semibold tracking-tight text-neutral-100">
              Create alert rule
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded p-1.5 text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10 transition-colors disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider mb-1.5 text-neutral-500">Match by</div>
            <div className="flex gap-1.5 mb-2 flex-wrap">
              {[
                { v: "bin",     l: "BIN" },
                { v: "issuer",  l: "Issuer bank" },
              ].map((o) => (
                <Chip key={o.v} active={criteriaType === o.v} onClick={() => setCriteriaType(o.v)}>{o.l}</Chip>
              ))}
            </div>
            {criteriaType === "bin" ? (
              <TextInput
                value={criteriaValue}
                onChange={setCriteriaValue}
                placeholder="e.g. 424242 or 424200-424299"
              />
            ) : (
              <IssuerPicker
                value={criteriaValue}
                onChange={setCriteriaValue}
              />
            )}
            {criteriaType === "bin" ? (
              <div className="mt-1 text-[11px] text-neutral-500">
                Single 6–8 digit BIN or a range (e.g. 424200-424299). Each tracked BIN counts toward your add-on quota.
              </div>
            ) : (
              <div className="mt-1 text-[11px] text-neutral-500">
                Pick from the list, or type to search. Names match exactly as picked.{" "}
                <span className="text-amber-300/90">Each issuer name counts as 50 BINs against your quota.</span>
              </div>
            )}
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-wider mb-1.5 text-neutral-500">
              Label <span className="text-neutral-600 normal-case">(optional)</span>
            </div>
            <TextInput value={label} onChange={setLabel} placeholder="e.g. Premium range" />
          </div>

          {/* Notify — same pattern as the Watchlists settings card.
              Primary is always the current user's email (read-only); a
              secondary recipient can be added, restricted to org members
              and gated by the Enterprise / Threat Intel tier. */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider mb-1.5 text-neutral-500">
              Notify
            </div>
            <input
              value={userEmail || ""}
              disabled
              readOnly
              placeholder={userEmail ? "" : "Loading your email…"}
              className="w-full rounded-md text-[13px] py-2 px-3 outline-none bg-[#000414]/60 border border-sky-900/40 text-neutral-300 placeholder:text-neutral-500 disabled:cursor-not-allowed"
            />
            <div className="mt-1 text-[11px] text-neutral-500">
              {hasOrg === false
                ? "Alerts go to your account email. You don't have an organization, so additional recipients aren't available."
                : "Alerts go to your account email. Optionally add a teammate from your organization."}
            </div>

            {/* Secondary recipient picker — only renders when "Add another"
                was pressed AND the tier+org gate allows it. */}
            {hasOrg !== false && multiNotifyAllowed && showSecondary && (
              <div className="mt-2 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <OrgMemberPicker
                    value={notifyEmail}
                    onChange={setNotifyEmail}
                    jwtToken={getJwtToken()}
                    enabled={multiNotifyAllowed && hasOrg !== false}
                    excludeEmail={userEmail}
                    placeholder="Select a teammate…"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setShowSecondary(false); setNotifyEmail(""); }}
                  title="Remove recipient"
                  className="p-1.5 rounded text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10 transition-colors shrink-0 mt-1"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {/* "Add another recipient" — hidden for solo (no-org) users.
                Shown disabled with an Enterprise+ pill for tiers that
                don't allow multi-notify, so the upgrade path is visible. */}
            {hasOrg !== false && !showSecondary && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => multiNotifyAllowed && setShowSecondary(true)}
                  disabled={!multiNotifyAllowed}
                  title={multiNotifyAllowed ? "Add another recipient" : "Enterprise / Threat Intelligence required"}
                  className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md border transition-colors ${
                    multiNotifyAllowed
                      ? "border-sky-900/40 bg-[#0a1224] text-neutral-300 hover:border-sky-700/50 hover:bg-sky-500/5 hover:text-neutral-100"
                      : "border-sky-900/30 bg-[#0a1224]/40 text-neutral-500 cursor-not-allowed"
                  }`}
                >
                  <Plus className="h-3 w-3" />
                  Add another recipient
                  {!multiNotifyAllowed && (
                    <span className="ml-1 inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset bg-amber-500/10 text-amber-300 ring-amber-500/30">
                      Enterprise+
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[11.5px] text-rose-300 whitespace-pre-line">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 mt-2 border-t border-sky-900/30">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-[12px] font-medium px-3 py-1.5 rounded border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded border border-sky-500/40 bg-sky-500/20 text-sky-100 hover:bg-sky-500/30 disabled:opacity-50 disabled:hover:bg-sky-500/20"
            >
              {submitting && (
                <span className="inline-block h-3 w-3 rounded-full border-2 border-sky-300 border-t-transparent animate-spin" />
              )}
              {submitting ? "Creating…" : "Create rule"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditAlertModal({ entry, onClose, onSaved, userEmail, hasOrg, multiNotifyAllowed }) {
  // Editable fields per the /paymentfraud/edit_card_watchlist.php contract:
  //   label, secondary_notification_email
  // entry_type / entry_value / notify_email render read-only at the top
  // so the analyst has context but can't change the watch target.
  const [label, setLabel] = useState(entry.label || "");
  const [notifyEmail, setNotifyEmail] = useState(entry.secondary_notification_email || "");
  // Whether the secondary picker is mounted. Defaults to visible when
  // the entry already had a secondary recipient — otherwise the user has
  // to click "Add another" to reveal it.
  const [showSecondary, setShowSecondary] = useState(
    !!(entry.secondary_notification_email && entry.secondary_notification_email.length > 0)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  const trimmedLabel = label.trim();
  const trimmedEmail = notifyEmail.trim();
  const originalLabel = entry.label || "";
  const originalEmail = entry.secondary_notification_email || "";
  const emailOk = !trimmedEmail || isValidEmail(trimmedEmail);

  // Build a partial patch — only include fields the user actually changed.
  // Empty string after a previously-set value is sent as null so the
  // backend can clear it; an always-empty field is omitted entirely.
  const buildPatch = () => {
    const patch = { id: entry.id };
    if (trimmedLabel !== originalLabel) patch.label = trimmedLabel || null;
    if (trimmedEmail !== originalEmail) patch.secondary_notification_email = trimmedEmail || null;
    return patch;
  };
  const patch = buildPatch();
  const hasChanges = Object.keys(patch).length > 1;   // > 1 because `id` is always present
  const canSubmit = hasChanges && emailOk && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(EDIT_CARD_WATCHLIST_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify(patch),
      });
      if (res.status === 401) { redirectToLogin(); return; }
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.success === false || payload?.error) {
        throw new Error(formatApiError(payload, `Could not save changes (${res.status}).`));
      }
      onSaved(payload.entry || null);
    } catch (err) {
      setError(err?.message || "Could not save changes.");
    } finally {
      setSubmitting(false);
    }
  };

  const labelByType = { bin: "BIN", issuer: "Issuer" };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60" onClick={submitting ? undefined : onClose} />
      <div className="relative w-full max-w-md rounded-md bg-[#0a1224] border border-sky-900/40 p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-sky-300/80">
              Edit rule
            </div>
            <h3 className="mt-1 text-[15px] font-semibold tracking-tight text-neutral-100">
              {entry.label || `${labelByType[entry.entry_type] || entry.entry_type} ${entry.entry_value}`}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded p-1.5 text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10 transition-colors disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {/* Immutable context — entry target + primary notification email */}
          <div className="rounded-md border border-sky-900/30 bg-[#000414]/50 px-3 py-2.5 space-y-1.5">
            <div className="flex items-baseline gap-3 text-[11.5px]">
              <span className="text-neutral-500 min-w-[88px]">Match by</span>
              <span className="text-neutral-200 font-mono">
                {labelByType[entry.entry_type] || entry.entry_type} = {entry.entry_value}
              </span>
            </div>
            <div className="flex items-baseline gap-3 text-[11.5px]">
              <span className="text-neutral-500 min-w-[88px]">Notify email</span>
              <span className="text-neutral-200 break-all">{entry.notify_email || "—"}</span>
            </div>
            <div className="text-[10.5px] text-neutral-500 italic pt-0.5">
              These fields can't be changed after creation.
            </div>
          </div>

          <div>
            <div className="text-[10.5px] uppercase tracking-wider mb-1.5 text-neutral-500">
              Label <span className="text-neutral-600 normal-case">(optional)</span>
            </div>
            <TextInput value={label} onChange={setLabel} placeholder="e.g. Premium range" />
            {originalLabel && trimmedLabel === "" && (
              <div className="mt-1 text-[11px] text-neutral-500">
                Leave empty to clear the label.
              </div>
            )}
          </div>

          {/* Notify — same pattern as the Watchlists settings card and
              the create modal. Primary email is the account email
              (immutable on this surface); secondary picks from org
              members and is gated by Enterprise / Threat Intel. */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider mb-1.5 text-neutral-500">
              Notify
            </div>
            <input
              value={userEmail || ""}
              disabled
              readOnly
              placeholder={userEmail ? "" : "Loading your email…"}
              className="w-full rounded-md text-[13px] py-2 px-3 outline-none bg-[#000414]/60 border border-sky-900/40 text-neutral-300 placeholder:text-neutral-500 disabled:cursor-not-allowed"
            />
            <div className="mt-1 text-[11px] text-neutral-500">
              {hasOrg === false
                ? "Alerts go to your account email. You don't have an organization, so additional recipients aren't available."
                : "Alerts go to your account email. Optionally add a teammate from your organization."}
            </div>

            {hasOrg !== false && multiNotifyAllowed && showSecondary && (
              <div className="mt-2 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <OrgMemberPicker
                    value={notifyEmail}
                    onChange={setNotifyEmail}
                    jwtToken={getJwtToken()}
                    enabled={multiNotifyAllowed && hasOrg !== false}
                    excludeEmail={userEmail}
                    placeholder="Select a teammate…"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setShowSecondary(false); setNotifyEmail(""); }}
                  title="Remove recipient"
                  className="p-1.5 rounded text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10 transition-colors shrink-0 mt-1"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {hasOrg !== false && !showSecondary && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => multiNotifyAllowed && setShowSecondary(true)}
                  disabled={!multiNotifyAllowed}
                  title={multiNotifyAllowed ? "Add another recipient" : "Enterprise / Threat Intelligence required"}
                  className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md border transition-colors ${
                    multiNotifyAllowed
                      ? "border-sky-900/40 bg-[#0a1224] text-neutral-300 hover:border-sky-700/50 hover:bg-sky-500/5 hover:text-neutral-100"
                      : "border-sky-900/30 bg-[#0a1224]/40 text-neutral-500 cursor-not-allowed"
                  }`}
                >
                  <Plus className="h-3 w-3" />
                  Add another recipient
                  {!multiNotifyAllowed && (
                    <span className="ml-1 inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset bg-amber-500/10 text-amber-300 ring-amber-500/30">
                      Enterprise+
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[11.5px] text-rose-300 whitespace-pre-line">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-3 mt-2 border-t border-sky-900/30">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="text-[12px] font-medium px-3 py-1.5 rounded border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded border border-sky-500/40 bg-sky-500/20 text-sky-100 hover:bg-sky-500/30 disabled:opacity-50 disabled:hover:bg-sky-500/20"
            >
              {submitting && (
                <span className="inline-block h-3 w-3 rounded-full border-2 border-sky-300 border-t-transparent animate-spin" />
              )}
              {submitting ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Slide-out detail panel for a single triggered alert. Mirrors the
 * DetailSidebar pattern from global search (sky border, [#06101e] bg,
 * right-anchored, max-w-5xl). Fetches /paymentfraud/get_card_alert.php
 * on open, supports server-paginated matched-cards table, and stays
 * mounted across openings so the slide-out animation reads naturally.
 *
 * Sections, top-to-bottom:
 *   Header        — close, status badge, sent_at, alert id
 *   Watchlist     — rule, who added it, when, last scan
 *   Notification  — primary/secondary recipients, delivery, sendgrid id
 *   Matched cards — paginated table of card_index / bin / last4 / issuer
 */
function AlertDetailSidebar({ open, alertId, onClose }) {
  const [alert, setAlert] = useState(null);
  const [cards, setCards] = useState(null);          // { total, page, page_size, results }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cardsPage, setCardsPage] = useState(1);
  const [retryNonce, setRetryNonce] = useState(0);
  const cardsPageSize = 50;
  // CSV export state — exports the ENTIRE matched-cards set, not just
  // the current page. We page through with the max page_size and
  // accumulate; `exportProgress` drives the in-button counter.
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ done: 0, total: 0 });
  const [exportError, setExportError] = useState("");
  const [copiedId, setCopiedId] = useState(false);

  // Reset to page 1 whenever a different alert is opened. Without this,
  // opening a 2-card alert after viewing a 200-card one would land on
  // an empty page.
  useEffect(() => { setCardsPage(1); }, [alertId]);

  useEffect(() => {
    if (!open || alertId == null) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(GET_CARD_ALERT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({
            id: alertId,
            cards_page: cardsPage,
            cards_page_size: cardsPageSize,
          }),
        });
        if (cancelled) return;
        if (res.status === 401) { redirectToLogin(); return; }
        const payload = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || payload?.success === false || payload?.error) {
          throw new Error(formatApiError(payload, `Could not load alert (${res.status}).`));
        }
        setAlert(payload.alert || null);
        setCards(payload.cards || null);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Could not load alert.");
          setAlert(null);
          setCards(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, alertId, cardsPage, retryNonce]);

  // Close on Escape, even when the load is still in flight.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Copy alert id to clipboard with a short-lived "Copied" affordance.
  const handleCopyId = () => {
    if (alertId == null) return;
    try { navigator.clipboard.writeText(String(alertId)); } catch {}
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 1500);
  };

  // Paginated full CSV export of matched cards. Calls the same endpoint
  // we use to render the table, but with the maximum page_size (200) and
  // iterates until we've collected all `total` rows. Builds a CSV
  // including watchlist context per row + an ISO timestamp alongside the
  // unix `exposed_at`. UTF-8 BOM is prepended so Excel picks up unicode.
  const handleDownloadCsv = async () => {
    if (alertId == null || exporting) return;
    setExporting(true);
    setExportError("");
    setExportProgress({ done: 0, total: 0 });
    try {
      const pageSize = 200;
      let page = 1;
      let collected = [];
      let total = 0;
      // Safety cap — even with 200/page, 50 pages = 10k rows is plenty
      // for a single alert. If the backend ever returns more we'd want
      // a streaming export instead.
      const maxPages = 50;
      while (page <= maxPages) {
        const res = await fetch(GET_CARD_ALERT_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({ id: alertId, cards_page: page, cards_page_size: pageSize }),
        });
        if (res.status === 401) { redirectToLogin(); return; }
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.success === false || payload?.error) {
          throw new Error(formatApiError(payload, `Could not export cards (${res.status}).`));
        }
        const results = payload?.cards?.results || [];
        total = payload?.cards?.total ?? total;
        collected = collected.concat(results);
        setExportProgress({ done: collected.length, total });
        if (results.length < pageSize || collected.length >= total) break;
        page += 1;
      }

      const w = (alert && alert.watchlist) || {};
      const cols = [
        "alert_id", "watchlist_id", "watchlist_entry_type", "watchlist_entry_value", "watchlist_label",
        "source_log_id", "card_index",
        "bin6", "bin8", "last_four",
        "issuer_bank", "issuer_country",
        "card_network", "card_type",
        "expiry_month", "expiry_year", "is_valid_expiry_now",
        "exposed_at_unix", "exposed_at_iso",
      ];
      const esc = (v) => {
        if (v == null) return "";
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const lines = [cols.join(",")];
      for (const c of collected) {
        const iso = Number.isFinite(c.exposed_at)
          ? new Date(c.exposed_at * 1000).toISOString()
          : "";
        const row = {
          alert_id: alertId,
          watchlist_id: w.id ?? "",
          watchlist_entry_type: w.entry_type ?? "",
          watchlist_entry_value: w.entry_value ?? "",
          watchlist_label: w.label ?? "",
          source_log_id: c.source_log_id ?? "",
          card_index: c.card_index ?? "",
          bin6: c.bin6 ?? "",
          bin8: c.bin8 ?? "",
          last_four: c.last_four ?? "",
          issuer_bank: c.issuer_bank ?? "",
          issuer_country: c.issuer_country ?? "",
          card_network: c.card_network ?? "",
          card_type: c.card_type ?? "",
          expiry_month: c.expiry_month ?? "",
          expiry_year: c.expiry_year ?? "",
          is_valid_expiry_now: c.is_valid_expiry_now ? "true" : "false",
          exposed_at_unix: c.exposed_at ?? "",
          exposed_at_iso: iso,
        };
        lines.push(cols.map((k) => esc(row[k])).join(","));
      }
      const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `alert-${alertId}-cards-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e?.message || "Could not export cards.");
    } finally {
      setExporting(false);
      setExportProgress({ done: 0, total: 0 });
    }
  };

  // Helpers — same time formatters used in the list. Section + Field
  // shapes match the global-search DetailSidebar so the two slide-outs
  // feel like the same surface.
  const labelByType = { bin: "BIN", issuer: "Issuer" };
  const tsMs = (n) => (Number.isFinite(n) ? n * 1000 : null);
  // Field — stacked label/value cell used inside a Section's grid. Matches
  // the DetailSidebar.Field pattern: uppercase tracking-wider label,
  // 13px value below.
  const Field = ({ label, children, mono }) => (
    <div className="min-w-0">
      <div className="text-xs font-medium uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className={`mt-1 text-[13px] text-neutral-200 break-words ${mono ? "font-mono" : ""}`}>
        {children}
      </div>
    </div>
  );
  // Section — title eyebrow (neutral, uppercase, tracking-wide) + optional
  // right-slot for inline actions. No top border — sections are separated
  // by space-y-8 on the body wrapper, same as DetailSidebar.
  const Section = ({ title, children, right }) => (
    <section>
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h3 className="text-[13px] font-semibold tracking-wide text-neutral-400 uppercase">
          {title}
        </h3>
        {right}
      </div>
      {children}
    </section>
  );

  const w = alert?.watchlist || {};
  const wTitle = w.label || `${labelByType[w.entry_type] || w.entry_type || "—"} = ${w.entry_value ?? "—"}`;
  const wSubtitle = w.label
    ? `${labelByType[w.entry_type] || w.entry_type || "—"} = ${w.entry_value ?? "—"}`
    : null;
  const sentMs = tsMs(alert?.sent_at);
  const createdMs = tsMs(w?.created_at);
  const scanMs = tsMs(w?.last_scan_at);

  const totalCards = cards?.total ?? 0;
  const totalCardPages = Math.max(1, Math.ceil(totalCards / cardsPageSize));
  const cardStart = totalCards === 0 ? 0 : (cardsPage - 1) * cardsPageSize + 1;
  const cardEnd = Math.min(cardsPage * cardsPageSize, totalCards);

  return (
    <div
      className={`fixed inset-0 z-[80] ${open ? "" : "pointer-events-none"}`}
      role="dialog"
      aria-modal="true"
      aria-hidden={!open}
    >
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-5xl border-l border-sky-900/40 bg-[#06101e] shadow-[0_0_50px_rgba(0,0,0,0.5)] transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"} flex flex-col`}
      >
        {/* Header — matches the DetailSidebar pattern from global search:
            small plain-text eyebrow + 16px title, action buttons grouped
            on the right as compact icon controls. */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-sky-900/30 gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[13px] text-neutral-400">
              <Bell className="h-4 w-4" />
              <span>Alert detail</span>
              {alertId != null && (
                <span className="font-mono text-neutral-500">#{alertId}</span>
              )}
            </div>
            <h2 className="mt-1 text-[16px] font-semibold tracking-tight text-white break-words">
              {alert ? wTitle : (loading ? "Loading…" : "—")}
            </h2>
            {alert && wSubtitle && (
              <div className="mt-0.5 text-[12px] font-mono text-neutral-500 break-all">
                {wSubtitle}
              </div>
            )}
            {alert && (
              <div className="mt-2 flex items-center gap-2 flex-wrap text-[12px]">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                  alert.delivered
                    ? "bg-sky-500/20 text-sky-300"
                    : "bg-rose-500/20 text-rose-300"
                }`}>
                  {alert.delivered ? "Delivered" : "Failed"}
                </span>
                {sentMs && (
                  <span className="text-neutral-500" title={fmtAbsolute(sentMs)}>
                    Sent {fmtRelative(sentMs)} <span className="text-neutral-600">·</span> {fmtAbsolute(sentMs)}
                  </span>
                )}
              </div>
            )}
            {/* Export error — surfaced near the trigger so the analyst
                sees why the download didn't happen. */}
            {exportError && (
              <div className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/5 px-2.5 py-1.5 text-[11px] text-rose-300 whitespace-pre-line">
                {exportError}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {alert && (
              <>
                <button
                  type="button"
                  onClick={handleDownloadCsv}
                  disabled={exporting || totalCards === 0}
                  title={totalCards === 0 ? "No cards to export" : "Download all matched cards as CSV"}
                  className="inline-flex items-center gap-1.5 rounded p-2 text-gray-300 hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {exporting ? (
                    <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-sky-300 border-t-transparent animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  <span className="hidden sm:inline text-[11.5px] font-medium">
                    {exporting
                      ? (exportProgress.total > 0
                          ? `${exportProgress.done} / ${exportProgress.total}`
                          : "Exporting…")
                      : "CSV"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleCopyId}
                  title="Copy alert ID"
                  className="rounded p-2 text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  {copiedId ? <Check className="h-4 w-4 text-sky-300" /> : <Copy className="h-4 w-4" />}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="rounded p-2 text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
              title="Close (Esc)"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading && !alert ? (
            <div className="px-5 py-12 text-center">
              <div className="inline-flex items-center gap-2 text-[12px] text-neutral-500">
                <span className="inline-block h-3 w-3 rounded-full border-2 border-sky-300 border-t-transparent animate-spin" />
                Loading alert…
              </div>
            </div>
          ) : error ? (
            <div className="px-5 py-8 text-center">
              <div className="text-[12px] text-rose-300 mb-3 whitespace-pre-line">{error}</div>
              <button
                onClick={() => setRetryNonce((n) => n + 1)}
                className="text-[11.5px] px-3 py-1 rounded border border-sky-900/40 text-neutral-200 hover:bg-sky-500/5"
              >
                Retry
              </button>
            </div>
          ) : alert ? (
            <>
              {/* Headline metrics — single divider-strip. Calm sub-captions
                  describe what each number actually counts (intentionally
                  no provenance references). */}
              <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-sky-900/30 border-b border-sky-900/30 bg-[#0a1224]/40">
                <div className="px-5 py-3.5">
                  <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500">
                    Cards matched
                  </div>
                  <div className="mt-1 text-[22px] font-semibold tabular-nums text-neutral-100 leading-none">
                    {(alert.cards_matched ?? 0).toLocaleString()}
                  </div>
                  <div className="mt-1 text-[10.5px] text-neutral-500">in this alert</div>
                </div>
                <div className="px-5 py-3.5">
                  <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500">
                    Sources
                  </div>
                  <div className="mt-1 text-[22px] font-semibold tabular-nums text-neutral-100 leading-none">
                    {(alert.matched_log_ids_count ?? 0).toLocaleString()}
                  </div>
                  <div className="mt-1 text-[10.5px] text-neutral-500">distinct sources</div>
                </div>
                <div className="px-5 py-3.5">
                  <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-neutral-500">
                    Currently in feed
                  </div>
                  <div className="mt-1 text-[22px] font-semibold tabular-nums text-neutral-100 leading-none">
                    {totalCards.toLocaleString()}
                  </div>
                  <div className="mt-1 text-[10.5px] text-neutral-500">
                    {totalCards > (alert.cards_matched ?? 0)
                      ? `+${totalCards - (alert.cards_matched ?? 0)} since alert`
                      : "no new matches"}
                  </div>
                </div>
              </div>

              {/* Body sections — uniform px-5 py-5 wrapper with space-y-8
                  between sections, matching the global-search sidebar. */}
              <div className="px-5 py-5 space-y-8">

              {/* Watchlist context — DetailSidebar pattern: grid of
                  stacked label/value cells, responsive cols. */}
              <Section
                title="Watchlist"
                right={
                  Number.isFinite(w.id) && (
                    <span className="text-xs font-mono text-neutral-500">
                      #{w.id}
                    </span>
                  )
                }
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                  <Field label="Rule">
                    {labelByType[w.entry_type] || w.entry_type || "—"}{" "}
                    <span className="font-mono text-neutral-300">= {w.entry_value ?? "—"}</span>
                    {w.label && (
                      <span className="ml-2 text-neutral-500">· {w.label}</span>
                    )}
                  </Field>
                  <Field label="Status">
                    {w.is_active === false ? (
                      <span className="inline-flex items-center rounded-full bg-neutral-700/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-400">
                        Paused
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-sky-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-300">
                        Active
                      </span>
                    )}
                  </Field>
                  <Field label="Added by">
                    {w.added_by?.email ? (
                      <>
                        {w.added_by.email}
                        {Number.isFinite(w.added_by.user_id) && (
                          <span className="ml-1 text-neutral-500 font-mono">#{w.added_by.user_id}</span>
                        )}
                      </>
                    ) : "—"}
                  </Field>
                  <Field label="Created">
                    {createdMs
                      ? <span title={fmtAbsolute(createdMs)}>{fmtRelative(createdMs)} <span className="text-neutral-500">· {fmtAbsolute(createdMs)}</span></span>
                      : "—"}
                  </Field>
                  <Field label="Last scan">
                    {scanMs
                      ? <span title={fmtAbsolute(scanMs)}>{fmtRelative(scanMs)} <span className="text-neutral-500">· {fmtAbsolute(scanMs)}</span></span>
                      : "—"}
                  </Field>
                </div>
              </Section>

              {/* Notification — primary/secondary, delivery state, full
                  error if applicable. SendGrid id removed per request. */}
              <Section title="Notification">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                  <Field label="Primary">{alert.notify_email || "—"}</Field>
                  <Field label="Secondary">{alert.secondary_notification_email || "—"}</Field>
                  <Field label="Sent">
                    {sentMs
                      ? <span title={fmtAbsolute(sentMs)}>{fmtRelative(sentMs)} <span className="text-neutral-500">· {fmtAbsolute(sentMs)}</span></span>
                      : "—"}
                  </Field>
                </div>
                {!alert.delivered && alert.error_message && (
                  <div className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[12px] text-rose-300 whitespace-pre-line">
                    {alert.error_message}
                  </div>
                )}
              </Section>

              {/* Matched cards — server-paginated table. The headline
                  here is the actual list of exposed cards; everything
                  above this section is context. */}
              <Section
                title="Matched cards"
                right={
                  totalCards > 0 && (
                    <span className="text-[10.5px] text-neutral-500 tabular-nums">
                      {cardStart}–{cardEnd} of {totalCards}
                    </span>
                  )
                }
              >
                {/* Sample BIN-6 strip — distinct BIN-6s the alert touched,
                    surfaced near the top of the cards section so an
                    analyst can spot the prefixes at a glance. */}
                {Array.isArray(alert.sample_bin6s) && alert.sample_bin6s.length > 0 && (
                  <div className="mb-3 flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-500">
                      Sample BIN-6
                    </span>
                    {alert.sample_bin6s.map((b) => (
                      <span
                        key={b}
                        className="inline-flex items-center font-mono tabular-nums text-[10.5px] rounded px-1.5 py-0.5 ring-1 ring-inset bg-neutral-500/10 text-neutral-300 ring-neutral-500/30"
                      >
                        {b}
                      </span>
                    ))}
                  </div>
                )}

                {totalCards === 0 ? (
                  <div className="text-[12px] text-neutral-500">No matched cards on this page.</div>
                ) : (
                  <div className="overflow-x-auto rounded-md border border-sky-900/30">
                    <table className="w-full text-[11.5px] border-collapse">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-[0.08em] text-neutral-500 bg-[#0a1224]/60">
                          <th className="text-left px-3 py-2 font-semibold">Card</th>
                          <th className="text-left px-3 py-2 font-semibold">Issuer</th>
                          <th className="text-left px-3 py-2 font-semibold">Network · Type</th>
                          <th className="text-left px-3 py-2 font-semibold">Expiry</th>
                          <th className="text-left px-3 py-2 font-semibold">Exposed</th>
                          <th className="text-left px-3 py-2 font-semibold">Source</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-sky-900/15">
                        {(cards?.results || []).map((c, i) => {
                          const expMs = tsMs(c.exposed_at);
                          const hasExpiry = Number.isFinite(c.expiry_month) && Number.isFinite(c.expiry_year);
                          const expiryStr = hasExpiry
                            ? `${String(c.expiry_month).padStart(2, "0")}/${c.expiry_year}`
                            : "—";
                          // Three explicit states: valid now (sky), expired
                          // (neutral), unknown (neutral italic). The old
                          // "expired" label was misleading whenever the
                          // expiry data was simply absent.
                          const expiryStatus = !hasExpiry
                            ? { text: "expiry unknown", cls: "text-neutral-500 italic" }
                            : c.is_valid_expiry_now
                              ? { text: "valid now", cls: "text-sky-300" }
                              : { text: "expired", cls: "text-neutral-500" };
                          return (
                            <tr key={`${c.source_log_id}-${c.card_index}-${i}`} className="hover:bg-sky-500/[0.03]">
                              <td className="px-3 py-2 align-top">
                                <div className="font-mono text-neutral-100 tabular-nums">
                                  {c.bin8 || c.bin6 || "—"}
                                  {c.last_four && (
                                    <span className="text-neutral-500"> · ••••{c.last_four}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div className="text-neutral-200 break-words">{c.issuer_bank || "—"}</div>
                                {c.issuer_country && (
                                  <div className="mt-1 flex items-center gap-1.5">
                                    <Globe className="h-3 w-3 text-neutral-500 shrink-0" />
                                    <span className="inline-flex items-center font-mono tabular-nums text-[10.5px] uppercase tracking-[0.08em] font-semibold rounded px-1.5 py-0.5 ring-1 ring-inset bg-neutral-500/10 text-neutral-300 ring-neutral-500/30">
                                      {c.issuer_country}
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div className="text-neutral-200">{c.card_network || "—"}</div>
                                {c.card_type && (
                                  <div className="text-[10px] text-neutral-500 mt-0.5 capitalize">{c.card_type}</div>
                                )}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div className="font-mono text-neutral-200 tabular-nums">{expiryStr}</div>
                                <div className={`text-[10px] mt-0.5 ${expiryStatus.cls}`}>
                                  {expiryStatus.text}
                                </div>
                              </td>
                              <td className="px-3 py-2 align-top text-neutral-300" title={expMs ? fmtAbsolute(expMs) : ""}>
                                {expMs ? fmtRelative(expMs) : "—"}
                              </td>
                              <td className="px-3 py-2 align-top">
                                <div className="font-mono text-[10.5px] text-neutral-400 break-all">
                                  {c.source_log_id ?? "—"}
                                </div>
                                {Number.isFinite(c.card_index) && (
                                  <div className="text-[10px] text-neutral-500 mt-0.5">idx {c.card_index}</div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Matched-cards pagination */}
                {totalCards > cardsPageSize && (
                  <div className="mt-3 flex items-center justify-end gap-1 text-[11.5px]">
                    <button
                      type="button"
                      disabled={cardsPage <= 1 || loading}
                      onClick={() => setCardsPage((p) => Math.max(1, p - 1))}
                      className="px-2.5 py-1 rounded border border-sky-900/40 text-neutral-200 hover:bg-sky-500/5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Prev
                    </button>
                    <div className="px-2.5 py-1 text-neutral-400">
                      Page <span className="text-neutral-200 tabular-nums">{cardsPage}</span> / <span className="tabular-nums">{totalCardPages}</span>
                    </div>
                    <button
                      type="button"
                      disabled={cardsPage >= totalCardPages || loading}
                      onClick={() => setCardsPage((p) => p + 1)}
                      className="px-2.5 py-1 rounded border border-sky-900/40 text-neutral-200 hover:bg-sky-500/5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
              </Section>
              </div>
            </>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   ISSUERS TAB
   ────────────────────────────────────────────────────────────────────────── */

function IssuersTab({ onJumpToCards }) {
  // Sort keys map 1:1 to the backend's `sort` enum: `name | country |
  // total_exposed | new_30d | currently_valid`.
  const [sort, setSort] = useState({ key: "total_exposed", dir: "desc" });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  // Live search — debounced via `debouncedSearch` so we don't fetch on
  // every keystroke. Sent as `?q=<query>` to the issuers endpoint.
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);

  // 350ms debounce — issuers endpoint is metadata-only, so we don't need
  // the 2s cooldown the in-form picker uses.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever the search query actually changes.
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set("page", String(page));
    p.set("page_size", String(perPage));
    p.set("sort", sort.key);
    p.set("sort_dir", sort.dir);
    if (debouncedSearch) p.set("q", debouncedSearch);
    return p.toString();
  }, [page, perPage, sort, debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setUpgradeRequired(false);

    fetch(`${ISSUERS_URL}?${queryString}`, {
      headers: { Authorization: `Bearer ${getJwtToken()}` },
    })
      .then(async (r) => {
        // 401 / 419 / 440 → session is gone. redirectToLogin() preserves
        // the current path via ?next=, so the user lands back here.
        if ([401, 419, 440].includes(r.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${r.status}`);
        }
        const json = await r.json().catch(() => ({}));
        if (r.status === 402 && json?.upgrade_required) throw { _upgrade: true };
        if (!r.ok) throw new Error(json?.message || `HTTP ${r.status}`);
        return json;
      })
      .then((json) => {
        if (cancelled) return;
        if (json?.success) setData(json);
        else setError(json?.message || "Failed to load issuers.");
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?._upgrade) setUpgradeRequired(true);
        else {
          console.error("Issuers fetch error:", e);
          setError(e?.message || "Network error");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [queryString]);

  const rows = data?.rows || [];
  const totalMatching = data?.total_matching ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalMatching / perPage));
  const safePage = Math.min(page, totalPages);

  const sortHeader = (key, label) => {
    const active = sort.key === key;
    return (
      <button
        type="button"
        onClick={() => {
          setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
          setPage(1);
        }}
        className={`inline-flex items-center gap-1 hover:text-sky-300 transition-colors ${
          active ? "text-sky-200" : "text-neutral-300"
        }`}
      >
        {label}
        {active && (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </button>
    );
  };

  // Tier upsell takes over the tab — same pattern as Card Information.
  if (upgradeRequired) {
    return <UpgradeRequiredCta />;
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-200 flex items-start justify-between gap-3">
          <span className="min-w-0">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-[11px] text-neutral-400 hover:text-white shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      <SurfaceCard noPad className="relative">
        <CardHeader
          icon={Building2}
          title="Top issuers leaderboard"
          right={
            <div className="flex items-center gap-2">
              {loading && data && (
                <span className="inline-flex items-center gap-1 text-[11px] text-sky-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse" />
                  Updating…
                </span>
              )}
              {totalMatching > 0 && (
                <span className="text-[11px] text-neutral-500 tabular-nums">
                  {fmtNum(totalMatching)} {debouncedSearch
                    ? `match${totalMatching === 1 ? "" : "es"}`
                    : "issuers"}
                </span>
              )}
              {/* Inline search — debounced live search against ?q= */}
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search issuers…"
                  className="w-full rounded-md text-[12px] py-1 pl-8 pr-8 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 placeholder:text-neutral-500 focus:border-sky-500/50"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput("")}
                    title="Clear search"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-neutral-400 hover:text-neutral-100 hover:bg-sky-500/10"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          }
        />
        {/* Indeterminate progress bar during refetch */}
        {loading && data && (
          <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden z-20">
            <div className="absolute inset-0 bg-sky-500/70 animate-pulse" />
          </div>
        )}

        <div className={`overflow-x-auto transition-opacity ${loading && data ? "opacity-60" : "opacity-100"}`}>
          <table className="w-full text-[12px] border-collapse">
            <thead className="bg-[#0b1426]/95">
              <tr className="text-neutral-300 border-b border-sky-900/30">
                <th className="text-left px-4 py-2.5 font-semibold uppercase tracking-wider text-[10.5px] w-8">#</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">{sortHeader("name", "Bank")}</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">{sortHeader("country", "Country")}</th>
                <th className="text-right px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">{sortHeader("total_exposed", "Total exposed")}</th>
                <th className="text-right px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">{sortHeader("new_30d", "New · 30d")}</th>
                <th className="text-right px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">{sortHeader("currently_valid", "Currently valid")}</th>
                <th className="text-left px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px]">Networks</th>
                <th className="text-right px-3 py-2.5 font-semibold uppercase tracking-wider text-[10.5px] w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && !data && (
                // Initial-load skeleton — 10 placeholder rows so the table
                // height doesn't collapse before the first response.
                Array.from({ length: 10 }).map((_, i) => (
                  <tr key={`skel-${i}`} className="border-b border-sky-900/20">
                    {Array.from({ length: 8 }).map((__, j) => (
                      <td key={j} className="px-3 py-2.5">
                        <div className="h-2.5 bg-sky-900/30 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="text-center px-4 py-10 text-[12px] text-neutral-500">No issuers.</td></tr>
              )}
              {rows.map((r) => {
                // `delta_30d` is a signed integer percent; positive means
                // exposure is growing (bad → red), negative means cooling
                // (good → emerald).
                const d = r.delta_30d;
                const up = d > 0, dn = d < 0;
                const deltaCls = up ? "text-red-400" : dn ? "text-emerald-400" : "text-neutral-500";
                return (
                  <tr
                    key={`${r.name}-${r.country}-${r.rank}`}
                    className="transition-colors border-b border-sky-900/20 hover:bg-sky-500/5"
                  >
                    <td className="px-4 py-2.5 tabular-nums text-neutral-500">{r.rank}</td>
                    <td className="px-3 py-2.5 text-neutral-100">{r.name}</td>
                    <td className="px-3 py-2.5"><Country cc={r.country} /></td>
                    <td className="px-3 py-2.5 text-right tabular-nums font-medium text-neutral-100">{fmtNum(r.total_exposed)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <span className={`inline-flex items-center gap-1 justify-end ${deltaCls}`}>
                        {up ? <ArrowUpRight className="h-3 w-3" /> : dn ? <ArrowDownRight className="h-3 w-3" /> : null}
                        {fmtNum(r.new_30d)}
                        {d != null && (
                          <span className="text-[10.5px] text-neutral-500 ml-1">
                            ({d > 0 ? "+" : ""}{d}%)
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-neutral-100">{fmtNum(r.currently_valid)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {(r.networks || []).slice(0, 4).map((n) => <NetworkBadge key={n} net={n} size="sm" />)}
                        {(r.networks || []).length > 4 && (
                          <span className="text-[10.5px] text-neutral-500">+{r.networks.length - 4}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => onJumpToCards && onJumpToCards(r.name)}
                        className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2 py-1 rounded border border-sky-900/40 text-sky-200 hover:bg-sky-500/10 hover:border-sky-700/50 transition-colors"
                        title={`Open Card Information filtered by ${r.name}`}
                      >
                        See cards
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-sky-900/30 text-[12px] text-neutral-400">
          <div className="flex items-center gap-2">
            Rows
            <select
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              className="rounded px-1.5 py-1 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100"
            >
              {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="tabular-nums">
            {totalMatching === 0
              ? "0"
              : `${(safePage - 1) * perPage + 1}–${Math.min(safePage * perPage, totalMatching)} of ${fmtNum(totalMatching)}`}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="px-2 py-1 rounded text-[12px] border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              Prev
            </button>
            <div className="px-2 tabular-nums text-neutral-100">{safePage} / {totalPages}</div>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="px-2 py-1 rounded text-[12px] border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5 disabled:opacity-30 disabled:hover:bg-transparent"
            >
              Next
            </button>
          </div>
        </div>
      </SurfaceCard>
    </div>
  );
}

/* IssuerDrilldown removed — the leaderboard rows used to navigate to a
   client-aggregated per-issuer view, but the new server-side leaderboard
   doesn't have a per-issuer detail endpoint yet. Re-add when one exists. */

/* ──────────────────────────────────────────────────────────────────────────
   BIN LOOKUP TAB
   ────────────────────────────────────────────────────────────────────────── */

function BinLookupTab() {
  const [bin, setBin] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);

  const trimmed = bin.trim();
  const isValid = /^\d{6}$/.test(trimmed);
  const canSubmit = isValid && !loading;

  const runLookup = () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    setUpgradeRequired(false);

    fetch(`${BIN_LOOKUP_URL}?bin=${encodeURIComponent(trimmed)}`, {
      headers: { Authorization: `Bearer ${getJwtToken()}` },
    })
      .then(async (r) => {
        if ([401, 419, 440].includes(r.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${r.status}`);
        }
        const json = await r.json().catch(() => ({}));
        if (r.status === 402 && json?.upgrade_required) throw { _upgrade: true };
        if (!r.ok) throw new Error(json?.message || `HTTP ${r.status}`);
        return json;
      })
      .then((json) => {
        if (json?.success) setData(json);
        else setError(json?.message || "Lookup failed.");
      })
      .catch((e) => {
        if (e?._upgrade) setUpgradeRequired(true);
        else {
          console.error("BIN lookup error:", e);
          setError(e?.message || "Network error");
        }
      })
      .finally(() => setLoading(false));
  };

  if (upgradeRequired) {
    return <UpgradeRequiredCta />;
  }

  // Response shape: { success, api_call_made, quota, rate_limit, result }.
  // `result` is a single object (not an array) for single-BIN lookups.
  const result = data?.result;

  return (
    <div className="space-y-4 max-w-2xl">
      {/* Lookup form */}
      <SurfaceCard noPad>
        <CardHeader icon={Search} title="BIN lookup" />
        <div className="p-5 space-y-3">
          <p className="text-[12.5px] text-neutral-400 leading-relaxed">
            Enter a 6-digit BIN to identify its network, type, issuer, and country.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              inputMode="numeric"
              value={bin}
              onChange={(e) => setBin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => { if (e.key === "Enter") runLookup(); }}
              placeholder="e.g. 421764"
              className="flex-1 rounded-md text-[14px] py-2 px-3 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 font-mono tabular-nums placeholder:text-neutral-500 focus:border-sky-500/50"
            />
            <button
              type="button"
              onClick={runLookup}
              disabled={!canSubmit}
              className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3.5 py-2 rounded-md border transition-colors ${
                canSubmit
                  ? "bg-sky-500/15 border-sky-500/40 text-sky-100 hover:bg-sky-500/25"
                  : "bg-[#0a1224] border-sky-900/40 text-neutral-500 cursor-not-allowed"
              }`}
              title={
                canSubmit
                  ? "Look up — uses 1 monthly quota if not cached"
                  : "BIN must be 6 digits"
              }
            >
              <Search className="h-3.5 w-3.5" />
              {loading ? "Looking up…" : "Look up"}
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 text-[11px] text-neutral-500">

            {trimmed.length > 0 && !isValid && (
              <span className="text-amber-300">Must be 6 digits.</span>
            )}
          </div>
        </div>
      </SurfaceCard>

      {error && !result && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-200 flex items-start justify-between gap-3">
          <span className="min-w-0">{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-[11px] text-neutral-400 hover:text-white shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {result && (
        <SurfaceCard noPad>
          <CardHeader icon={CreditCard} title="Result" />
          <ul className="divide-y divide-sky-900/20">
            <BinLookupResultRow row={result} />
          </ul>
        </SurfaceCard>
      )}

    </div>
  );
}

// Single result row for the BIN lookup. Each row shows the BIN, its source
// (cache vs live vs miss), and metadata in a tight horizontal layout. Misses
// are dimmed and labelled clearly so the user knows the call was made but
// no data came back.
function BinLookupResultRow({ row }) {
  const isMiss = row?.found === false || /miss/i.test(row?.source || "");
  const sourceLabel =
    row?.source === "cache"      ? "Cache" :
    row?.source === "api_ninjas" ? "Live"  :
    isMiss                       ? "Not found" :
    row?.source || "—";
  const sourceCls =
    row?.source === "cache"
      ? "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30"
      : isMiss
      ? "bg-neutral-500/10 text-neutral-400 ring-neutral-500/30"
      : "bg-sky-500/10 text-sky-300 ring-sky-500/30";

  return (
    <li className={`grid grid-cols-[1fr_auto] gap-x-4 gap-y-2 px-4 py-3 ${isMiss ? "opacity-60" : ""}`}>
      {/* Left: BIN + metadata */}
      <div className="min-w-0 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono tabular-nums text-[14px] text-neutral-100">{row.bin}</span>
          {row.card_network && <NetworkBadge net={row.card_network} size="sm" />}
          <span
            className={`inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset ${sourceCls}`}
          >
            {sourceLabel}
          </span>
        </div>
        {isMiss ? (
          <div className="text-[11.5px] text-neutral-500 italic">
            No record returned for this BIN.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11.5px]">
              <div>
                <span className="text-neutral-500 mr-1">Issuer:</span>
                {row.issuer_bank
                  ? <span className="text-neutral-200">{row.issuer_bank}</span>
                  : <span className="text-neutral-500 italic">Unknown</span>}
              </div>
              <div>
                <span className="text-neutral-500 mr-1">Country:</span>
                {row.issuer_country
                  ? <span className="inline-flex items-center gap-1.5"><Country cc={row.issuer_country} /></span>
                  : <span className="text-neutral-500">—</span>}
              </div>
              <div>
                <span className="text-neutral-500 mr-1">Type:</span>
                {row.card_type
                  ? <span className="text-neutral-200 capitalize">{row.card_type}</span>
                  : <span className="text-neutral-500">—</span>}
              </div>
              <div>
                <span className="text-neutral-500 mr-1">Brand:</span>
                {row.card_brand
                  ? <span className="text-neutral-200">{row.card_brand}</span>
                  : <span className="text-neutral-500">—</span>}
              </div>
              {row.card_tier && (
                <div>
                  <span className="text-neutral-500 mr-1">Tier:</span>
                  <span className="text-neutral-200 capitalize">{row.card_tier}</span>
                </div>
              )}
            </div>
            {Array.isArray(row.categories) && row.categories.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap pt-1">
                <span className="text-[10.5px] uppercase tracking-wider text-neutral-500">Categories</span>
                {row.categories.map((c) => (
                  <span
                    key={c}
                    className="inline-flex items-center text-[10px] font-medium rounded px-1.5 py-0.5 ring-1 ring-inset bg-sky-500/10 text-sky-300 ring-sky-500/30 capitalize"
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </li>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ────────────────────────────────────────────────────────────────────────── */

const TABS = [
  { id: "overview",     label: "Overview" },
  { id: "card_intel",   label: "Card Information" },
  { id: "issuers",      label: "Issuers" },
  { id: "bin_lookup",   label: "BIN Lookup" },
  { id: "alerts",       label: "Alerts" },
];

const RANGES = [
  { v: "24h", l: "24h",  days: 1 },
  { v: "7d",  l: "7d",   days: 7 },
  { v: "30d", l: "30d",  days: 30 },
  { v: "90d", l: "90d",  days: 90 },
  { v: "1y",  l: "1y",   days: 365 },
  { v: "all", l: "All",  days: 3650 },
];

export default function PaymentFraud() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Active tab is mirrored to ?active=<id> so deep links + refreshes land on
  // the same tab. Default "overview" is omitted from the URL.
  const [active, setActive] = useState(() => {
    if (typeof window === "undefined") return "overview";
    const t = new URLSearchParams(window.location.search).get("active");
    return TABS.some((tab) => tab.id === t) ? t : "overview";
  });
  const [range, setRange] = useState("30d");

  // Module-level entitlement: only the Overview tab is free. Card Info,
  // Issuers, BIN Lookup, and Alerts are add-on-only and get a soft-locked
  // appearance + a modal CTA when clicked.
  const pfAccess = usePaymentFraudAccess();
  const pfLocked = pfAccess.available === false;
  const isTabLocked = (tabId) => pfLocked && tabId !== "overview";
  // Domain of the modal CTA — the label of the tab the user just tried to
  // open. `null` means closed.
  const [lockedTabPrompt, setLockedTabPrompt] = useState(null);

  // If the user landed on a locked tab via a stored URL (?active=alerts) while
  // the entitlement says no — bounce them back to Overview and pop the modal.
  useEffect(() => {
    if (!pfAccess.loading && isTabLocked(active)) {
      const wanted = TABS.find((t) => t.id === active);
      setActive("overview");
      setLockedTabPrompt(wanted?.label || "this tab");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pfAccess.loading, pfLocked]);

  // Keep ?active in sync as the user switches tabs. We use replaceState so
  // each tab change doesn't pile up entries in the browser history.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (active && active !== "overview") params.set("active", active);
    else params.delete("active");
    const qs = params.toString();
    window.history.replaceState(
      {},
      "",
      window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash,
    );
  }, [active]);

  const rangeDays = (RANGES.find((r) => r.v === range) || RANGES[2]).days;

  // Cross-tab navigation helper — clicking "See cards" on an Issuers row
  // jumps to Card Information with the issuer name pre-applied as a filter.
  // We write the params to the URL synchronously BEFORE switching the tab so
  // CardIntelligenceTab's lazy initial state picks them up on first render.
  const jumpToCards = (issuerName) => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      params.set("active", "card_intel");
      if (issuerName) params.set("issuer", issuerName);
      else params.delete("issuer");
      window.history.replaceState(
        {},
        "",
        window.location.pathname + `?${params.toString()}` + window.location.hash,
      );
    }
    setActive("card_intel");
  };

  return (
    <div className="min-h-screen bg-[#000414] text-neutral-100 flex">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        activeKey="payment_fraud"
        onChange={() => {}}
      />

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Header — exact match to WatchlistEvents: same bg, border, shadow,
            and the px-6 h-14 inner strip. */}
        <header className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]">
          {/* On mobile the title + the date-range chip group fight for room
              and the title wraps to two/three lines. Letting the row wrap
              and putting the chips on their own row below the title is the
              cleanest fix: title gets full width, chips stay reachable. */}
          <div className="px-4 md:px-6 py-2.5 md:py-0 md:h-14 flex flex-wrap md:flex-nowrap items-center justify-between gap-2 md:gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <h1 className="text-[17px] md:text-[20px] font-bold leading-tight tracking-tight truncate">
                Payment Fraud Intelligence
              </h1>
            </div>

            <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
              <div className="inline-flex rounded overflow-hidden border border-sky-900/40 bg-[#0a1224]">
                {RANGES.map((r) => (
                  <button
                    key={r.v}
                    onClick={() => setRange(r.v)}
                    className={`text-[11.5px] px-2.5 py-1.5 transition-colors ${
                      range === r.v
                        ? "bg-sky-500/15 text-sky-100"
                        : "text-neutral-400 hover:bg-sky-500/5 hover:text-neutral-200"
                    }`}
                  >
                    {r.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>

        {/* Sub-nav strip — separate strip below header, exact WatchlistEvents
            pattern: bg-[#000414], border-b border-white/10, px-6 py-2. */}
        <div className="border-b border-white/10 bg-[#000414]">
          <div className="px-6 py-2">
            <nav className="flex items-center gap-5 flex-wrap" role="tablist" aria-label="Payment fraud sections">
              {TABS.map((t, i) => {
                const isActive = active === t.id;
                const tabLocked = isTabLocked(t.id);
                return (
                  <React.Fragment key={t.id}>
                    {i > 0 && <span aria-hidden className="h-3.5 w-px bg-neutral-700/60 -mb-px" />}
                    <button
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => {
                        if (tabLocked) {
                          setLockedTabPrompt(t.label);
                          return;
                        }
                        setActive(t.id);
                      }}
                      title={tabLocked ? `${t.label} requires the Payment Fraud add-on` : undefined}
                      className={`py-2 text-sm font-medium transition whitespace-nowrap -mb-px border-b-[3px] ${
                        isActive
                          ? "text-white border-neutral-200"
                          : "text-neutral-400 hover:text-white border-transparent"
                      }`}
                    >
                      {t.label}
                    </button>
                  </React.Fragment>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="flex-1 px-4 md:px-6 py-6">
          {active === "overview"   && <OverviewTab range={range} rangeDays={rangeDays} />}
          {active === "card_intel" && <CardIntelligenceTab />}
          {active === "alerts"     && <AlertsTab cards={MOCK_CARDS} />}
          {active === "issuers"    && <IssuersTab onJumpToCards={jumpToCards} />}
          {active === "bin_lookup" && <BinLookupTab />}
        </div>
      </main>

      {/* Locked-tab CTA — fires when a Researcher / Enterprise user without
          the Payment Fraud add-on clicks a Pro tab. Matches the in-app
          paywall look used elsewhere (sky icon chip, headline, two CTAs). */}
      {lockedTabPrompt && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
          onClick={() => setLockedTabPrompt(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`${lockedTabPrompt} requires the Payment Fraud add-on`}
        >
          <div
            className="relative w-full max-w-md rounded-md border border-sky-900/40 bg-[#0a1224] shadow-[0_30px_70px_-20px_rgba(0,0,0,0.85)] p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setLockedTabPrompt(null)}
              className="absolute top-2 right-2 rounded-md p-2 text-neutral-400 hover:text-white hover:bg-white/[0.06] transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="mx-auto inline-flex rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-3">
              <CreditCard className="h-6 w-6 text-sky-300" />
            </div>
            <h3 className="text-[16px] font-semibold text-white">
              {lockedTabPrompt} is an add-on feature
            </h3>
            <p className="mt-2 text-[13px] text-neutral-400 leading-relaxed">
              Compromised-card monitoring, issuer leaderboards, BIN lookup, and
              alerts ship as the Payment Fraud Intelligence add-on for{" "}
              <span className="text-sky-200 font-medium">Enterprise</span> and{" "}
              <span className="text-sky-200 font-medium">Threat Intelligence</span>{" "}
              — or included in{" "}
              <span className="text-sky-200 font-medium">Extended Threat Intelligence</span>.
            </p>
            <div className="mt-5 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setLockedTabPrompt(null);
                  navigate("/upgrades?tab=addons");
                }}
                className="inline-flex items-center gap-1.5 rounded-md border border-sky-600/40 bg-sky-500/15 px-3.5 py-1.5 text-[13px] font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
              >
                View add-ons
              </button>
              <button
                type="button"
                onClick={() => setLockedTabPrompt(null)}
                className="rounded-md border border-sky-900/40 px-3.5 py-1.5 text-[13px] text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
